/**
 * Piano turni mensile - definizione slot e assegnazione medici
 */

import {
  getState,
  canEditShifts,
  setShiftPlanWeekRange,
  assignDoctorToSlotByKey,
  unassignSlotByKey,
  swapAssignmentsByKey,
  moveAssignmentBetweenKeys,
  getAssignmentByKey,
  setShiftPlanBulk,
  clearShiftPlanForDateRange,
  lockWeek,
  unlockWeek,
  isWeekLocked,
  isDoctorAbsent,
  getDesiderataForDoctorDate,
  refreshShiftAssignmentsFromApi,
  setShiftImportIssues,
  getShiftImportIssueBySlotOrDate,
  canLockWeekArchives,
  getAutoLockManualAssignments,
  setAutoLockManualAssignments,
  addCalendarModification,
  deleteCalendarModification,
  deleteShiftSlot,
  getCreditedAbsenceHoursForRange,
  getHoursPerDayForDoctorOnDate,
  clearAbsenceDesiderataConflictForSlot,
  withStateBatch,
  canSeeWorkloadBimesterBalances,
  canSeePlannerAbsenceRow,
  canManagePlannerAbsenceRow,
} from '../store.js';
import { buildPlannerAbsenceChipsByDate } from '../plannerAbsenceRow.js';
import { showCellActionSheet } from './AbsencesCalendar.js';
import {
  TIME_SLOTS,
  TIME_SLOT_LABELS,
  zeroPercentWarningDistKey,
  zeroPercentWarningAreaLabel,
  SLOT_CATEGORY_LABELS,
  UOC_LABELS,
  CATEGORY_TO_UOC,
  badgeClassForSlotCategory,
  HOURS_PER_SLOT,
  HOURS_PER_DAY,
} from '../types.js';
import { getDaysInWeekRange, formatDateShort, getISOWeekString, getMondayOfWeek, getISOWeekYear, weekRangeSelectorHTML, getWeekRangeSelectorValue, setupWeekRangeSelector, refreshWeekSelectOptions, formatLevelingReport, confirmInModal, PDF_DOWNLOAD_ICON_HTML, getUniqueSlotName, canonicalizeSlotName, canonicalizeSlotNameKeepIndex, ensurePlannerSlotName, addCalendarDays, slotLogicalKeyString, stableCalendarSlotKey } from '../utils.js';
import { canAssignDoctorToSlot, resolveAssignmentForSlot, isDirezioneSlotKind } from '../validation.js';
import { apiGetUsers, apiSendShiftPlanPdfEmail, apiDownloadMyShiftCalendarIcs, apiDownloadShiftCalendarIcsForDoctor, apiDownloadShiftCalendarDeltaIcsForDoctor, apiAdminShiftPlanNotifyDelta, apiImportShiftPlanXlsx } from '../api.js';
import { exportShiftPlanToPDF } from '../pdfExport.js';

/**
 * True se l'utente ha almeno un recapito abilitato alla ricezione del piano turni.
 * L'espansione sui singoli indirizzi la fa il backend (recipientUserId).
 */
function hasShiftPdfRecipient(u) {
  const emails = Array.isArray(u?.emails) ? u.emails : null;
  if (emails && emails.length) {
    return emails.some((e) => e && String(e.email || '').trim() && e.recvShiftPdf !== false);
  }
  return !!String(u?.email || '').trim();
}
import { exportShiftPlanToExcel } from '../excelExport.js';
import { getSlotsForDates, getOfficialSlotsForDates, autoAssignSlots, autoAssignSlotsForPhase, getNightRestBoundaryDate, getSlotPhase } from '../shiftGenerator.js';
import { computeWeeklyBreakdownFromSlots, getFestiviCountInWeek, getWeekStart, computeWeeklyBalance, computeBimesterBalancesAtCutoffs, getBimesterForDate, doctorsForWorkloadBalance, isExcludedFromWorkloadBalance } from '../workload.js';
import {
  getCompactSlotLabel,
  getPlannerTableRowLabelKey,
  getSlotTypeRowLabel,
  getSlotTypesForWeek,
  isReperibilitaSlotType,
  isAnesthesiaAmbulatorySlotType,
  isNoraLikeSalaSlotType,
} from '../plannerTableLayout.js';

/** Payload impostato in dragstart; su alcuni browser dragend azzera le variabili prima che drop legga. */
function plannerDragPayloadFromDataTransfer(e, fallbackSlotId, fallbackDoctorId) {
  let slotId = fallbackSlotId;
  let doctorId = fallbackDoctorId;
  const apply = (raw) => {
    if (!raw || typeof raw !== 'string') return;
    const p = JSON.parse(raw);
    if (p.slotId != null && p.slotId !== '') slotId = p.slotId;
    if (p.doctorId != null && p.doctorId !== '') doctorId = p.doctorId;
  };
  try {
    apply(e.dataTransfer?.getData?.('application/json'));
    apply(e.dataTransfer?.getData?.('text/plain'));
  } catch (_) {}
  return {
    slotId,
    doctorId: doctorId != null && doctorId !== '' ? String(doctorId) : doctorId,
  };
}

/** Cella inner per hit-test DnD: il drop sul padding del <td> non ha .slot-cell-inner tra gli antenati del target. */
function plannerDnDTargetInner(ev) {
  const inner = ev.target.closest?.('.slot-cell-inner');
  if (inner) return inner;
  const cell = ev.target.closest?.('td.drop-zone');
  return cell?.querySelector('.slot-cell-inner') ?? null;
}

/** Giorno prima del primo lunedì del range: notte domenica → smonto lunedì mattino. */
function getPlannerNightRestBoundaryDate(weekFrom) {
  const wf = weekFrom || getMondayOfWeek(new Date().toISOString().slice(0, 10));
  return addCalendarDays(wf, -1);
}

/** Date visibili + giorno limite riposo post-notte + eventuali giorni da chiavi slot. */
function plannerDatesForValidation(weekFrom, weekTo, ...extraDates) {
  const defaultMon = getMondayOfWeek(new Date().toISOString().slice(0, 10));
  const wf = weekFrom || defaultMon;
  const wt = weekTo || wf;
  const dates = [...new Set(getDaysInWeekRange(wf, wt).map(x => x.date))];
  const boundary = getPlannerNightRestBoundaryDate(wf);
  if (!dates.includes(boundary)) dates.unshift(boundary);
  for (const raw of extraDates) {
    const d = String(raw || '').slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(d) && !dates.includes(d)) dates.push(d);
  }
  return dates;
}

function buildPlannerSlotsForValidation(weekFrom, weekTo, ...extraDates) {
  const dates = plannerDatesForValidation(weekFrom, weekTo, ...extraDates);
  return mergePlannerSlotsWithPersisted(dates, getSlotsForDates(dates));
}

/** Stessi slot del change-handler: estende le date se la chiave punta a un giorno fuori range (edge case). */
function buildAllSlotsForPlannerDrop(weekFrom, weekTo, ...slotKeys) {
  return buildPlannerSlotsForValidation(weekFrom, weekTo, ...slotKeys);
}

function mergePlannerSlotsWithPersisted(dates, generatedSlots) {
  void generatedSlots; // compatibilità chiamanti esistenti
  return getOfficialSlotsForDates(dates);
}

function buildSyntheticSlotFromKey(slotKey) {
  const raw = String(slotKey || '').trim();
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})-([^-]+)-([^-]+)-([^-]+)-(.+)$/);
  if (!m) return null;
  const date = m[1];
  const timeSlot = m[2];
  const category = m[3];
  const uocId = m[4] || '';
  const tail = m[5] || '';
  const firstToken = tail.split('-')[0] || category;
  const prettyName = String(firstToken).replace(/_/g, ' ').trim();
  const atSyn = timeSlot.startsWith('reperibilita') ? 'reperibilita' : 'clinico';
  const dt = new Date(`${date}T12:00:00`);
  const wd = dt.getDay();
  const weekday = wd === 0 ? 'dom' : ['lun', 'mar', 'mer', 'gio', 'ven', 'sab'][wd - 1];
  return {
    id: raw,
    slotKey: raw,
    name: ensurePlannerSlotName(category, prettyName, timeSlot, atSyn),
    category,
    activityType: atSyn,
    timeSlot,
    date,
    weekday,
    uocId,
  };
}

/** Rimuove l'evidenziazione drop su tutte le celle (evita outline/sfondo blu "appiccicati"). */
function clearPlannerDnDDragOver(root) {
  root?.querySelectorAll?.('.slot-cell-inner.drag-over')?.forEach((el) => el.classList.remove('drag-over'));
}

/** Evita re-entrata del listener change e loop (es. Firefox/Windows) quando si ripristina il valore del select. */
function revertPlannerSlotSelect(select, prevValue) {
  select.dataset.plannerSuppressChange = '1';
  try {
    select.value = prevValue;
  } finally {
    queueMicrotask(() => { delete select.dataset.plannerSuppressChange; });
  }
}

/**
 * Sfumatura sul bordo destro della griglia quando c'e' altro da scorrere: senza, in
 * verticale l'ultima colonna appare tagliata e sembra un difetto invece che uno scorrimento.
 * La maschera si applica al box dell'elemento, non al contenuto, quindi non scorre con esso.
 */
function syncWeekShiftsScrollFade(container) {
  container.querySelectorAll('.week-shifts').forEach((el) => {
    const update = () => {
      const resta = el.scrollWidth - el.clientWidth - el.scrollLeft;
      el.classList.toggle('has-scroll-right', resta > 2);
    };
    if (!el.dataset.scrollFadeBound) {
      el.dataset.scrollFadeBound = '1';
      el.addEventListener('scroll', update, { passive: true });
    }
    update();
  });
}

function syncWeekStickyOffsets(container) {
  const blocks = container.querySelectorAll('.week-block');
  blocks.forEach((block) => {
    const header = block.querySelector('.week-header');
    if (!header) return;
    const headerHeight = Math.ceil(header.getBoundingClientRect().height);
    const isMobileLike = window.matchMedia('(max-width: 900px), (pointer: coarse)').matches;
    const padding = isMobileLike ? 16 : 8;
    const minOffset = isMobileLike ? 72 : 52;
    const offset = Math.max(minOffset, headerHeight + padding);
    block.style.setProperty('--week-sticky-offset', `${offset}px`);
  });
}

/** Vista tabella: drag dopo ~0,1 s di pressione sulla sigla (click breve = tendina). draggable resta false fino al timer. */
const TABLE_DRAG_ARM_MS = 100;
let tableDragArmTimer = null;
let tableDragPointerDown = false;
let tableDragArmed = false;
let tableDragStarted = false;
let tableDragPointerBadge = null;
let tableSkipNextCellClick = false;
/** True mentre è in corso una mutazione di slot (rimozione/aggiunta) con re-render: i click
 * sulle celle vengono ignorati per evitare che, durante l'aggiornamento, il target si sposti
 * su una casella diversa (es. cella vuota → pop-up "crea slot"). */
let plannerBusy = false;
let plannerActiveCellEl = null;
/** Backup payload DnD (dragstart → drop) se getData/fallback non bastano. */
let plannerDnDLastPayload = null;
/** True se il drag è partito dalla vista tabella: keysRm raggruppa per etichetta riga, non per suffisso slotKey. */
let plannerDndCollapseAssignmentKeys = false;
let weekStickyResizeObserver = null;

const WEEK_REMOVE_TRASH_ICON_HTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" focusable="false"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>`;
const WEEK_BTN_SPINNER_ICON_HTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" focusable="false"><path d="M12 3a9 9 0 1 1-9 9"/></svg>`;
const WEEK_BULK_LOCK_CLOSED_ICON_HTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" focusable="false"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>`;
const WEEK_BULK_LOCK_OPEN_ICON_HTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" focusable="false"><path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/></svg>`;

function clearPlannerActiveCell(container) {
  if (plannerActiveCellEl && plannerActiveCellEl.isConnected) {
    plannerActiveCellEl.classList.remove('planner-active-cell');
    const td = plannerActiveCellEl.closest?.('td');
    if (td) td.classList.remove('planner-active-cell');
  }
  plannerActiveCellEl = null;
  container?.querySelectorAll?.('.planner-active-cell')?.forEach((el) => el.classList.remove('planner-active-cell'));
}

function setPlannerActiveCell(container, el) {
  if (!container || !el) return;
  if (plannerActiveCellEl && plannerActiveCellEl !== el) clearPlannerActiveCell(container);
  plannerActiveCellEl = el;
  el.classList.add('planner-active-cell');
  const td = el.closest?.('td');
  if (td) td.classList.add('planner-active-cell');
}

function setPlannerActiveCellFromModal(container, modalOrOverlayEl) {
  const src = modalOrOverlayEl?.dataset?.plannerActiveCellSrc || '';
  if (!src) return;
  const m = src.match(/^add-cell:(\d{4}-\d{2}-\d{2})\|([^|]+)\|([^|]+)\|([^|]*)\|(.*)$/);
  if (!m) return;
  const date = m[1];
  const timeSlot = m[2];
  const category = m[3];
  const uocId = m[4] || '';
  const name = m[5] || '';
  const sel = `.grid-cell-na.add-slot-cell[data-date="${date}"][data-time-slot="${timeSlot}"][data-category="${category}"][data-uoc-id="${uocId}"][data-name="${CSS.escape(name)}"]`;
  const td = container?.querySelector?.(sel);
  if (td) setPlannerActiveCell(container, td);
}

function formatDateItDash(dateStr) {
  const s = String(dateStr || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function getIssueDoctorSigla(issue, doctors = []) {
  const did = String(issue?.doctor_id || issue?.doctorId || '').trim();
  if (!did) return '';
  const d = (doctors || []).find((x) => String(x?.id || '') === did);
  return String(d?.sigla || '').trim();
}

const IMPORT_SOURCE_LABELS = Object.freeze({
  'xlsx-template': 'Excel esportato dall\u2019app',
  'xlsx-grid': 'tabellone Excel',
  pdf: 'tabellone PDF',
});

const MONTH_NAMES_IT = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

/** Sotto-specialità in forma breve: nel report la sigla basta a riconoscerla. */
const IMPORT_UOC_SHORT_LABELS = Object.freeze({
  uoc_anestesia_ti: 'UOC Anestesia/TI',
  uos_blocco_operatorio: 'UOS Blocco Operatorio',
  uos_medicina_dolore: 'UOS Medicina del Dolore',
});

/**
 * Righe di riepilogo su come è stato interpretato il documento importato.
 * Per i tabelloni storici è l'unico modo che ha l'utente di verificare che ogni
 * riga sia finita nello slot giusto.
 */
function describeImportedDocument(doc) {
  if (!doc || typeof doc !== 'object') return [];
  const lines = [];
  const source = IMPORT_SOURCE_LABELS[String(doc.source || '')] || String(doc.source || '');
  const month = Number(doc.month);
  const period = month >= 1 && month <= 12 && doc.year
    ? ` \u2014 ${MONTH_NAMES_IT[month - 1]} ${doc.year}`
    : '';
  if (source) lines.push(`Origine: ${source}${period}`);

  const mappings = Array.isArray(doc.mappings) ? doc.mappings : [];
  if (mappings.length) {
    lines.push('', 'Righe riconosciute nel documento:');
    for (const m of mappings) {
      const label = String(m?.label || '').trim() || '(senza etichetta)';
      const range = String(m?.timeRange || '').trim();
      const category = String(m?.category || '').replace(/_/g, ' ');
      const uoc = IMPORT_UOC_SHORT_LABELS[String(m?.uocId || '')] || '';
      lines.push(
        `- ${label}${range ? ` (${range})` : ''} \u2192 ${m?.slotName || '?'}`
        + ` \u00b7 ${m?.timeSlot || '?'} \u00b7 ${category}`
        + `${uoc ? ` \u00b7 ${uoc}` : ''} \u00b7 ${m?.days || 0} giorni`
      );
    }
  }

  const notes = Array.isArray(doc.notes) ? doc.notes.filter(Boolean) : [];
  if (notes.length) {
    lines.push('', 'Note di lettura:');
    for (const note of notes) lines.push(`- ${note}`);
  }
  if (lines.length) lines.push('');
  return lines;
}

/** Stato evidenziazione per settimana: weekStart -> Set(doctorId) */
const plannerWeekHighlightedDoctors = new Map();

/** Evita che setTimeout di render precedenti sovrascrivano le card ore/giorni (es. W23 stale). */
let plannerRenderGeneration = 0;
/** @type {Map<number, () => void>} */
const plannerRenderWaiters = new Map();

function resolvePlannerRenderWaiter(gen) {
  const resolve = plannerRenderWaiters.get(gen);
  if (!resolve) return;
  plannerRenderWaiters.delete(gen);
  try { resolve(); } catch (_) {}
}

/** Attende il setTimeout di paint di una generazione render (o la supersessione). */
function waitForPlannerRenderGeneration(gen) {
  if (gen !== plannerRenderGeneration) return Promise.resolve();
  if (plannerRenderWaiters.has(gen)) {
    return new Promise((resolve) => {
      const prev = plannerRenderWaiters.get(gen);
      plannerRenderWaiters.set(gen, () => {
        try { prev?.(); } catch (_) {}
        resolve();
      });
    });
  }
  return new Promise((resolve) => {
    plannerRenderWaiters.set(gen, resolve);
  });
}

const WEEK_STATS_HOURS_TOLERANCE = 2;

function statsTbodyForWeek(weekBlock, weekStart) {
  if (!weekBlock) return null;
  const id = `stats-${weekStart}`;
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return weekBlock.querySelector(`#${CSS.escape(id)}`);
  }
  return document.getElementById(id) || weekBlock.querySelector(`tbody[id="${id}"]`);
}

function getOrCreateWeekHighlightSet(weekStart) {
  const key = String(weekStart || '');
  let set = plannerWeekHighlightedDoctors.get(key);
  if (!set) {
    set = new Set();
    plannerWeekHighlightedDoctors.set(key, set);
  }
  return set;
}

function removeWeekHighlightSetIfEmpty(weekStart) {
  const key = String(weekStart || '');
  const set = plannerWeekHighlightedDoctors.get(key);
  if (set && set.size === 0) plannerWeekHighlightedDoctors.delete(key);
}

// Palette discreta ad alta separazione percettiva (evita blocchi di toni troppo simili).
// Ordine volutamente "saltato" per differenziare anche medici adiacenti in elenco.
const DOCTOR_HIGHLIGHT_PALETTE = Object.freeze([
  { bg: '#1D4ED8', fg: '#F8FAFC', border: '#93C5FD' }, // blue
  { bg: '#B91C1C', fg: '#FEF2F2', border: '#FCA5A5' }, // red
  { bg: '#047857', fg: '#ECFDF5', border: '#6EE7B7' }, // green
  { bg: '#7C3AED', fg: '#F5F3FF', border: '#C4B5FD' }, // purple
  { bg: '#C2410C', fg: '#FFF7ED', border: '#FDBA74' }, // orange
  { bg: '#0F766E', fg: '#F0FDFA', border: '#5EEAD4' }, // teal
  { bg: '#BE185D', fg: '#FDF2F8', border: '#F9A8D4' }, // pink
  { bg: '#365314', fg: '#F7FEE7', border: '#BEF264' }, // lime
  { bg: '#1E40AF', fg: '#EFF6FF', border: '#93C5FD' }, // blue-2
  { bg: '#7F1D1D', fg: '#FEF2F2', border: '#FCA5A5' }, // red-2
  { bg: '#166534', fg: '#F0FDF4', border: '#86EFAC' }, // green-2
  { bg: '#6D28D9', fg: '#F5F3FF', border: '#C4B5FD' }, // purple-2
  { bg: '#9A3412', fg: '#FFF7ED', border: '#FDBA74' }, // orange-2
  { bg: '#155E75', fg: '#ECFEFF', border: '#67E8F9' }, // cyan
  { bg: '#9D174D', fg: '#FDF2F8', border: '#FDA4AF' }, // rose
  { bg: '#3F6212', fg: '#F7FEE7', border: '#BEF264' }, // olive
  { bg: '#1E3A8A', fg: '#EFF6FF', border: '#93C5FD' }, // navy
  { bg: '#7C2D12', fg: '#FFF7ED', border: '#FDBA74' }, // burnt
  { bg: '#134E4A', fg: '#F0FDFA', border: '#5EEAD4' }, // sea
  { bg: '#831843', fg: '#FDF2F8', border: '#F9A8D4' }, // magenta-deep
]);

function _doctorSortKey(doc) {
  return [
    String(doc?.cognome || '').toLowerCase(),
    String(doc?.nome || '').toLowerCase(),
    String(doc?.sigla || '').toLowerCase(),
    String(doc?.id || ''),
  ].join('|');
}

function buildDoctorHighlightPaletteMap(doctors) {
  const ordered = [...(doctors || [])].sort((a, b) => _doctorSortKey(a).localeCompare(_doctorSortKey(b)));
  const map = new Map();
  const paletteLen = DOCTOR_HIGHLIGHT_PALETTE.length;
  ordered.forEach((doc, idx) => {
    if (!doc || doc.id == null) return;
    map.set(String(doc.id), DOCTOR_HIGHLIGHT_PALETTE[idx % paletteLen]);
  });
  return map;
}

function doctorHighlightPalette(doctorId, paletteMap) {
  const sid = String(doctorId || '');
  if (!sid) return null;
  return paletteMap.get(sid) || DOCTOR_HIGHLIGHT_PALETTE[0];
}

async function confirmDirezioneManualOverride(doctorId, slot) {
  const st = getState();
  const doctor = (st.doctors || []).find((d) => String(d.id) === String(doctorId));
  const doctorLabel = doctor ? `${doctor.sigla} ${doctor.cognome}` : 'questo medico';
  const dateLabel = formatDateShort(slot?.date || '');
  const slotLabel = slot?.name || (SLOT_CATEGORY_LABELS[slot?.category] || 'Direzione');
  const timeLabel = TIME_SLOT_LABELS[slot?.timeSlot] || slot?.timeSlot || 'prolungato';
  return confirmInModal(
    `Per i turni Direzione la competenza dipende solo dall'Abilità Direzione sul profilo medico.\n\n`
      + `Il medico ${doctorLabel} non ha questa abilitazione (equivalente a competenza 0% per Direzione): `
      + `non sarà mai assegnato automaticamente; può essere inserito solo manualmente con questa conferma.\n\n`
      + `Confermi l'assegnazione manuale al turno "${slotLabel}" del ${dateLabel} (${timeLabel})?`,
    { title: 'Abilità Direzione', okLabel: 'Conferma', cancelLabel: 'Annulla' },
  );
}

function confirmNightRestSmontoManualOverride() {
  return confirmInModal(
    'Attenzione! Stai assegnando un turno sullo smonto notte. Confermi?',
    { title: 'Smonto post-notte', okLabel: 'Conferma', cancelLabel: 'Annulla' },
  );
}

function confirmNightConsecutiveManualOverride() {
  return confirmInModal(
    'Attenzione! Il medico fa notte il giorno precedente o successivo: stai assegnando notti consecutive. Confermi?',
    { title: 'Notti consecutive', okLabel: 'Conferma', cancelLabel: 'Annulla' },
  );
}

async function confirmAbsenceDesiderataManualOverride(doctorId, slot) {
  const st = getState();
  const doctor = (st.doctors || []).find((d) => String(d.id) === String(doctorId));
  const doctorLabel = doctor ? `${doctor.sigla} ${doctor.cognome}` : 'questo medico';
  const dateLabel = formatDateShort(slot?.date || '');
  const timeLabel = TIME_SLOT_LABELS[slot?.timeSlot] || slot?.timeSlot || '';
  return confirmInModal(
    `${doctorLabel} ha un'incompatibilità (assenza giustificata o desiderata) per il turno del ${dateLabel} (${timeLabel}).\n\n`
      + `Confermando, il turno verrà assegnato e il giorno di assenza/desiderata in conflitto verrà rimosso dal piano assenze.\n\n`
      + `Confermi l'assegnazione manuale?`,
    { title: 'Incompatibilità assenza/desiderata', okLabel: 'Conferma e rimuovi', cancelLabel: 'Annulla' },
  );
}

/** True se l'unico ostacolo è lo smonto P/N e con override diventa assegnabile (stesso medico già confermato sulle sorelle). */
function needsNightRestSmontoManualSlot(doctorId, slot, allSlots, assignmentsByKey, maxDaysPerWeek) {
  const base = {
    slots: allSlots,
    assignmentsByKey,
    allowManualOverride: true,
    allowOverrideZeroPercent: true,
    maxDaysPerWeek,
  };
  const r0 = canAssignDoctorToSlot(doctorId, slot, base);
  if (r0.ok) return false;
  if (r0.reasonCode !== 'night_rest_smonto') return false;
  return canAssignDoctorToSlot(doctorId, slot, { ...base, allowNightRestSmontoManualOverride: true }).ok;
}

/**
 * Parte dello slotKey dopo la data (YYYY-MM-DD-): distingue template/modifiche e UoC omonime.
 * Senza questo, più slot reali condividono la stessa cella in tabella e il DnD salva su un slotKey
 * mentre il re-render può mostrarne un altro (cella "vuota" con — ma non più slot-uncovered).
 */
function plannerTableRowSuffixFromSlotKey(sk) {
  if (!sk || typeof sk !== 'string') return '';
  const m = sk.match(/^\d{4}-\d{2}-\d{2}-(.+)$/);
  return m ? m[1] : sk;
}

/** Tutte le chiavi in byKey che sono lo stesso posto del calendario della sorgente (template/DB diversi). */
function assignmentKeysForSamePlannerRow(sourceSlot, fromSlotKey, allSlots, byKey, collapseByLabel = false) {
  const out = new Set();
  if (fromSlotKey) out.add(fromSlotKey);
  if (!sourceSlot) return [...out];
  const sourceDoctorId = (
    (fromSlotKey && byKey?.[fromSlotKey] != null && byKey?.[fromSlotKey] !== '')
      ? byKey[fromSlotKey]
      : resolveAssignmentForSlot(sourceSlot, byKey || {})
  );
  if (collapseByLabel) {
    if (allSlots) {
      const labelKey = getPlannerTableRowLabelKey(sourceSlot);
      for (const slotK of allSlots) {
        if (slotK.date !== sourceSlot.date) continue;
        if (getPlannerTableRowLabelKey(slotK) !== labelKey) continue;
        const k = slotK.slotKey || slotK.id;
        if (!k) continue;
        if (sourceDoctorId != null && sourceDoctorId !== '') {
          const doctorOnKey = (
            byKey?.[k] != null && byKey?.[k] !== ''
              ? byKey[k]
              : resolveAssignmentForSlot(slotK, byKey || {})
          );
          if (String(doctorOnKey ?? '') !== String(sourceDoctorId)) continue;
        }
        if (k) out.add(k);
      }
    }
    return [...out];
  }
  if (!byKey) return [...out];
  const suf = plannerTableRowSuffixFromSlotKey(sourceSlot.slotKey || sourceSlot.id);
  for (const k of Object.keys(byKey)) {
    if (out.has(k)) continue;
    const slotK = allSlots.find(s => (s.slotKey || s.id) === k);
    if (!slotK) continue;
    if (slotK.date === sourceSlot.date
      && slotK.timeSlot === sourceSlot.timeSlot
      && slotK.category === sourceSlot.category
      && String(slotK.uocId || '') === String(sourceSlot.uocId || '')
      && plannerTableRowSuffixFromSlotKey(slotK.slotKey || slotK.id) === suf) {
      out.add(k);
    }
  }
  return [...out];
}

/** Slot nella stessa cella logica (vista tabella): stessa data e stessa etichetta riga. */
function plannerTableSlotsSameLabelRow(slot, allSlots) {
  if (!slot || !allSlots?.length) return [];
  const labelKey = getPlannerTableRowLabelKey(slot);
  return allSlots.filter(s => s.date === slot.date && getPlannerTableRowLabelKey(s) === labelKey);
}

/** Allinea assegnazioni/rimozioni agli altri slot DB con la stessa etichetta riga (vista tabella). */
async function syncPlannerTableSlotSiblingsFromPrimary(slot, primaryKey, doctorId, allSlots) {
  const sibSlots = plannerTableSlotsSameLabelRow(slot, allSlots).filter(s => (s.slotKey || s.id) !== primaryKey);
  if (!sibSlots.length) return;
  const [yrS] = slot.date.split('-').map(Number);
  const maxDaysS = getFestiviCountInWeek(getWeekStart(slot.date), yrS) > 0 ? 4 : 5;
  const dirPersistNoFlag = (() => {
    const d = getState().doctors.find(x => String(x.id) === String(doctorId));
    return Boolean(d && !d.abilitaDirezione);
  })();
  for (const sib of sibSlots) {
    const k = sib.slotKey || sib.id;
    if (doctorId) {
      const sim = { ...getState().shiftAssignmentsByKey, [k]: doctorId };
      const needDirOverride = isDirezioneSlotKind(sib) && dirPersistNoFlag;
      const needNightSmonto = needsNightRestSmontoManualSlot(doctorId, sib, allSlots, sim, maxDaysS);
      const resS = canAssignDoctorToSlot(doctorId, sib, {
        slots: allSlots,
        assignmentsByKey: sim,
        allowManualOverride: true,
        allowOverrideZeroPercent: true,
        maxDaysPerWeek: maxDaysS,
        // Override solo se stesso medico senza Abilità Direzione (già confermato sulla cella principale).
        allowDirezioneManualOverride: needDirOverride,
        allowNightRestSmontoManualOverride: needNightSmonto,
      });
      if (!resS.ok) continue;
      const persistSib = {};
      if (needDirOverride) persistSib.allowDirezioneManualOverride = true;
      if (needNightSmonto) persistSib.allowNightRestSmontoManualOverride = true;
      await assignDoctorToSlotByKey(k, doctorId, sib, getAutoLockManualAssignments(), persistSib);
    } else {
      await unassignSlotByKey(k, sib);
    }
  }
}

function doctorSiglaById(doctorId) {
  if (doctorId == null || doctorId === '') return '';
  const sid = String(doctorId);
  const d = getState().doctors.find(x => String(x.id) === sid);
  return d?.sigla ? String(d.sigla) : '';
}

/** True se non si può usare drag-and-drop su questo slot (settimana archiviata o turno lucchettato). */
function isPlannerSlotKeyDnDLocked(slotKey) {
  if (slotKey == null || String(slotKey).trim() === '') return true;
  const st = getState();
  const dateStr = String(slotKey).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr) && isWeekLocked(getWeekStart(dateStr))) return true;
  if (st.lockedAssignmentsByKey?.[slotKey]) return true;
  return false;
}

/** Messaggio DnD: solo le sigle dei medici coinvolti nell'incompatibilità (una o due se entrambi falliscono su destinazione). */
function formatDndIncompatibilityAlert(reason, doctorIds) {
  const ids = [...new Set((doctorIds || []).filter(Boolean))];
  const sigs = ids.map(doctorSiglaById).filter(Boolean);
  const tail = sigs.length ? ` (${sigs.join(', ')})` : '';
  return `${reason || 'Operazione non consentita'}${tail}`;
}

/**
 * Popola le option della tendina alla prima apertura della cella. Idempotente: una volta
 * generate restano fino al prossimo render della griglia, cosi' `revertPlannerSlotSelect`
 * trova il valore da ripristinare anche quando il rollback arriva dopo un modale di
 * conferma e la cella nel frattempo si e' chiusa.
 */
function ensurePlannerSlotSelectOptions(select) {
  if (!select || select.dataset.optionsPending !== '1') return;
  const slotKey = select.dataset.slotKey;
  if (!slotKey) return;
  const st = getState();
  const defaultMon = getMondayOfWeek(new Date().toISOString().slice(0, 10));
  // Stesso intervallo con cui la griglia e' stata renderizzata (vedi renderShiftPlanner):
  // gli slot del range sono il contesto su cui si valutano i vincoli del medico.
  const wf = st.shiftPlanWeekFrom || defaultMon;
  const wt = st.shiftPlanWeekTo || defaultMon;
  const allSlots = buildPlannerSlotsForValidation(wf, wt, slotKey);
  const slot = allSlots.find(s => (s.slotKey || s.id) === slotKey) || buildSyntheticSlotFromKey(slotKey);
  // Slot introvabile: la cella resta col solo segnaposto (si puo' liberare, non riassegnare)
  // e il flag non viene rimosso, cosi' la prossima apertura riprova.
  if (!slot) return;
  select.innerHTML = buildSlotAssignOptionsHTML(slot, allSlots);
  delete select.dataset.optionsPending;
}

function openTableSlotAssignSelect(cellInner, select) {
  if (!cellInner || !select || select.disabled) return;
  ensurePlannerSlotSelectOptions(select);
  cellInner.setAttribute('data-open', '1');
  const n = Math.max(1, select.options.length);
  select.setAttribute('size', String(Math.min(n, 14)));
  try { select.focus(); } catch (_) {}
}

function closeTableSlotAssignSelect(inner) {
  if (!inner) return;
  inner.removeAttribute('data-open');
  const sel = inner.querySelector('.slot-assign-select');
  if (sel) sel.removeAttribute('size');
}

function escapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function ordinalBaseFallback(category) {
  const map = {
    terapia_intensiva: 'TI',
    sala_operatoria: 'SO',
    nora: 'NORA',
    medicina_dolore: 'MedDol',
    cure_palliative: 'CP',
    formazione: 'FOR',
    direzione: 'DIR',
  };
  return map[category] || getCompactSlotLabel({ category }) || 'Posto';
}

function computeNextOrdinalNameForDay(dayGroupSlots, category, baseHint = '', timeSlot = null, activityType = null) {
  const desired = canonicalizeSlotName(category, baseHint, timeSlot, activityType)
    || String(baseHint || '').trim()
    || ordinalBaseFallback(category);
  const existingNames = (dayGroupSlots || [])
    .map((s) => canonicalizeSlotNameKeepIndex(s?.category, s?.name, s?.timeSlot, s?.activityType) || String(s?.name || '').trim())
    .filter(Boolean);
  return getUniqueSlotName(desired, existingNames) || desired;
}

function getWeekBulkLockState(weekStart, weekSlots, assignmentsByKey, lockedAssignmentsByKey) {
  const assigned = getPlannerCanonicalAssignedTargets(weekSlots, assignmentsByKey);
  const assignedCount = assigned.length;
  const lockedCount = assigned.filter((t) => Boolean(lockedAssignmentsByKey[t.key])).length;
  const shouldUnlock = assignedCount > 0 && lockedCount === assignedCount;
  return {
    weekStart,
    assignedCount,
    lockedCount,
    shouldUnlock,
    targetLocked: !shouldUnlock,
  };
}

function getPlannerCanonicalAssignedTargets(weekSlots, assignmentsByKey) {
  const byDateLabel = new Map();
  for (const s of weekSlots || []) {
    const date = String(s?.date || '').trim();
    const key = String(s?.slotKey || s?.id || '').trim();
    if (!date || !key) continue;
    const label = getPlannerTableRowLabelKey(s);
    const groupKey = `${date}\u001f${label}`;
    if (!byDateLabel.has(groupKey)) byDateLabel.set(groupKey, []);
    byDateLabel.get(groupKey).push(s);
  }

  const targets = [];
  for (const arr of byDateLabel.values()) {
    const sorted = [...arr].sort((a, b) => String(a.slotKey || a.id).localeCompare(String(b.slotKey || b.id)));
    let canonical = sorted[0];
    for (const cand of sorted) {
      const did = resolveAssignmentForSlot(cand, assignmentsByKey || {});
      if (did != null && did !== '') {
        canonical = cand;
        break;
      }
    }
    const key = canonical?.slotKey || canonical?.id;
    const doctorId = resolveAssignmentForSlot(canonical, assignmentsByKey || {});
    if (!key || !doctorId) continue;
    targets.push({ slot: canonical, key, doctorId });
  }
  return targets;
}

/** Slot della settimana senza medico (celle vuote nella griglia): candidati a «Rimuovi slot non assegnati». */
function getPlannerUnassignedSlotsToRemove(weekSlots, assignmentsByKey) {
  const byDateLabel = new Map();
  for (const s of weekSlots || []) {
    const date = String(s?.date || '').trim();
    const key = String(s?.slotKey || s?.id || '').trim();
    if (!date || !key) continue;
    const label = getPlannerTableRowLabelKey(s);
    const groupKey = `${date}\u001f${label}`;
    if (!byDateLabel.has(groupKey)) byDateLabel.set(groupKey, []);
    byDateLabel.get(groupKey).push(s);
  }
  const out = [];
  const seen = new Set();
  for (const arr of byDateLabel.values()) {
    const hasAssigned = arr.some((s) => {
      const did = resolveAssignmentForSlot(s, assignmentsByKey || {});
      return did != null && did !== '';
    });
    if (hasAssigned) continue;
    for (const s of arr) {
      const k = String(s.slotKey || s.id || '').trim();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(s);
    }
  }
  return out;
}

function getWeekHeaderActionsHTML({ weekStart, weekLocked, canEdit, canArchiveWeek, showWeekBulkBtn, weekBulk }) {
  const actions = [];
  if (canArchiveWeek && canEdit) {
    actions.push(
      `<button type="button" class="btn btn-sm week-lock-btn" data-week="${weekStart}" aria-label="${weekLocked ? 'Sblocca settimana archiviata' : 'Blocca e archivia settimana'}" title="${weekLocked ? 'Sblocca settimana' : 'Blocca e archivia settimana (approvazione definitiva)'}">${weekLocked ? '🔓' : '🔒'} <span class="week-btn-label">${weekLocked ? 'Sblocca settimana archiviata' : 'Blocca e archivia settimana'}</span><span class="week-btn-label-short">${weekLocked ? 'Sblocca' : 'Archivia'}</span></button>`
    );
  } else if (canEdit && !canArchiveWeek && weekLocked) {
    actions.push('<span class="week-lock-readonly" title="Settimana archiviata — solo admin e direttore possono sbloccare">🔒</span>');
  }
  if (showWeekBulkBtn) {
    const archived = Boolean(weekLocked);
    const noAssigned = !weekBulk || weekBulk.assignedCount === 0;
    const allLocked = Boolean(weekBulk?.assignedCount && weekBulk.lockedCount === weekBulk.assignedCount);
    const noneLocked = !weekBulk || weekBulk.lockedCount === 0;
    const lockDisabled = archived || noAssigned || allLocked;
    const unlockDisabled = archived || noAssigned || noneLocked;
    const archivedTitle = 'Settimana archiviata: sblocca prima il lucchetto ISO';
    actions.push(
      `<button type="button" class="btn btn-sm week-bulk-lock-btn week-bulk-lock-btn--lock" data-week="${weekStart}" data-target-locked="1" ${lockDisabled ? 'disabled' : ''} aria-label="Blocca tutti gli slot assegnati" title="${archived ? archivedTitle : (allLocked ? 'Tutti gli slot assegnati sono già bloccati' : 'Blocca tutti gli slot assegnati della settimana')}"><span class="week-btn-icon" aria-hidden="true">${WEEK_BULK_LOCK_CLOSED_ICON_HTML}</span><span class="week-btn-label">Blocca tutti</span></button>`
    );
    actions.push(
      `<button type="button" class="btn btn-sm week-bulk-lock-btn week-bulk-lock-btn--unlock" data-week="${weekStart}" data-target-locked="0" ${unlockDisabled ? 'disabled' : ''} aria-label="Sblocca tutti gli slot assegnati" title="${archived ? archivedTitle : (noneLocked ? 'Nessuno slot assegnato è bloccato' : 'Sblocca tutti gli slot assegnati della settimana')}"><span class="week-btn-icon" aria-hidden="true">${WEEK_BULK_LOCK_OPEN_ICON_HTML}</span><span class="week-btn-label">Sblocca tutti</span></button>`
    );
    actions.push(
      `<button type="button" class="btn btn-sm week-add-slot-btn" data-week="${weekStart}" ${weekLocked ? 'disabled' : ''} aria-label="Aggiungi slot" title="${weekLocked ? 'Settimana archiviata: aggiunta slot disabilitata' : 'Aggiungi slot (anche su righe non presenti)'}"><span class="week-btn-icon" aria-hidden="true">＋</span><span class="week-btn-label">Aggiungi slot</span></button>`
    );
    actions.push(
      `<button type="button" class="btn btn-sm week-remove-empty-slots-btn" data-week="${weekStart}" ${weekLocked ? 'disabled' : ''} aria-label="Rimuovi slot non assegnati" title="${weekLocked ? 'Settimana archiviata: rimozione disabilitata' : 'Elimina gli slot della settimana senza medico assegnato'}"><span class="week-btn-icon week-btn-icon--trash" aria-hidden="true">${WEEK_REMOVE_TRASH_ICON_HTML}</span><span class="week-btn-icon week-btn-icon--spinner" aria-hidden="true">${WEEK_BTN_SPINNER_ICON_HTML}</span><span class="week-btn-label">Rimuovi slot non assegnati</span></button>`
    );
  }
  if (!actions.length) return '';
  return `<div class="week-header-actions">${actions.join('')}</div>`;
}

/** Barra bilancio settimanale (domanda slot vs disponibilità) sotto l'intestazione di ogni settimana del Piano turni. Stessa logica del Calendario (computeWeeklyBalance). */
function getWeekBalanceBarHTML(weekStart) {
  const b = computeWeeklyBalance(weekStart);
  const balanceClass = b.diff >= 0 ? 'balance-ok' : 'balance-deficit';
  const title = 'Domanda = slot ufficiali della settimana ISO (calendario+DB, deduplicati), esclusi reperibilità e Direzione: somma pesi fascia (M/P=1, Pr=1,2, N=2), ogni unità di slot = 6h. Disponibilità = per ogni medico attivo senza Abilità Direzione: (5 giorni − festivi) × (debito orario settimanale ÷ 5) − assenze giustificate. Slot disponibili = disponibilità ore ÷ 6h.';
  const sign = (n) => `${n >= 0 ? '+' : ''}${n}`;
  const tile = (label, value, extra = '') =>
    `<div class="week-balance-tile"><span class="week-balance-label">${label}</span>`
    + `<span class="week-balance-value">${value}</span>`
    + (extra ? `<span class="week-balance-extra">${extra}</span>` : '')
    + `</div>`;
  // Tre riquadri invece di una frase con sei numeri e "Bilancio" ripetuto due volte:
  // in verticale la frase occupava tre righe e in orizzontale finiva sotto l'header sticky.
  return `<div class="balance-indicator week-balance-bar ${balanceClass}" title="${title}">`
    + tile('Ore', `${b.scheduled.toFixed(0)} / ${b.availability.toFixed(0)} h`, 'da coprire / disponibili')
    + tile('Slot', `${b.scheduledSlots.toFixed(1)} / ${b.availabilitySlots.toFixed(1)}`, 'da coprire / disponibili')
    + tile('Bilancio', `${sign(b.diff.toFixed(0))} h`, `${sign(b.balanceSlots.toFixed(1))} slot${b.diff < 0 ? ' · straordinari' : ''}`)
    + `</div>`;
}

async function removePlannerSlotAndRefresh(container, slot) {
  if (!slot) return;
  plannerBusy = true;
  try {
    await removePlannerSlotCore(slot);
    await refreshShiftAssignmentsFromApi();
    await renderShiftPlanner(container);
  } finally {
    // Rilascia il blocco solo al ciclo successivo: scarta eventuali click "fantasma"
    // emessi a fine render sull'ultima posizione.
    setTimeout(() => { plannerBusy = false; }, 0);
  }
}

/** Rimuove uno slot dal piano (DB e, se applicabile, modifica calendario one-off). Nessun re-render.
 * @param {object} slot
 * @param {{ skipRefresh?: boolean }} [opts] - skipRefresh: non refresh API dopo ogni remove (bulk).
 */
async function removePlannerSlotCore(slot, opts = {}) {
  if (!slot) return;
  const skipRefresh = Boolean(opts.skipRefresh);
  const modOpts = skipRefresh ? { skipRefresh: true } : {};
  const normName = (v) => String(v || '').toLowerCase().replace(/[\s_-]+/g, '');
  const slotKey = String(slot.slotKey || slot.id || '').trim();
  const slotNameEnsured = ensurePlannerSlotName(
    slot.category,
    slot.name,
    slot.timeSlot,
    slot.activityType || 'clinico',
  );
  const resolvedUocId = String(
    slot.uocId || CATEGORY_TO_UOC[slot.category] || '',
  ).trim();

  const findPersistedMatches = () => {
    const persisted = getState().shiftSlots || [];
    const matches = [];
    const seen = new Set();
    for (const s of persisted) {
      if (!s?.id || seen.has(s.id)) continue;
      const sk = String(s.slotKey || s.id || '').trim();
      const sameKey = slotKey && (sk === slotKey || String(s.id) === slotKey);
      const sameUoc = (
        String(s.uocId || '').trim() === resolvedUocId
        || !String(s.uocId || '').trim()
        || !resolvedUocId
      );
      const sameIdentity = (
        String(s.date || '') === String(slot.date || '')
        && String(s.timeSlot || '') === String(slot.timeSlot || '')
        && String(s.category || '') === String(slot.category || '')
        && sameUoc
        && (
          normName(s.name) === normName(slot.name)
          || normName(s.name) === normName(slotNameEnsured)
          || slotLogicalKeyString(s) === slotLogicalKeyString(slot)
        )
      );
      if (sameKey || sameIdentity) {
        seen.add(s.id);
        matches.push(s);
      }
    }
    return matches;
  };

  // 1) Elimina eventuali righe DB (dopo solver spesso presenti anche se lo slot nasce dal calendario).
  for (const row of findPersistedMatches()) {
    try {
      await deleteShiftSlot(row.id);
    } catch (err) {
      console.warn('Eliminazione slot DB fallita:', row.id, err);
    }
  }

  // 2) Slot creati con "＋Aggiungi slot": basta togliere la modifica add.
  if (slot._oneOffModId) {
    await deleteCalendarModification(slot._oneOffModId, modOpts);
    return;
  }

  // 3) Blocca la rigenerazione dal template/calendario.
  // uocId deve essere quello del template (es. uos_blocco_operatorio): se resta '' la remove
  // non matcha SO2/MedDol2 e lo slot ricompare vuoto (caso W32 SO2 mattino).
  const removeNames = [...new Set([
    String(slot.name || '').trim(),
    String(slotNameEnsured || '').trim(),
  ].filter(Boolean))];
  if (!removeNames.length) removeNames.push(slotNameEnsured || 'SO1');

  for (const name of removeNames) {
    await addCalendarModification({
      modificationType: 'remove',
      scope: 'one_off',
      dateSpecific: slot.date,
      dateFrom: slot.date,
      timeSlot: slot.timeSlot,
      category: slot.category,
      uocId: resolvedUocId,
      name,
      activityType: slot.activityType || 'clinico',
    }, modOpts);
  }

  const stillThere = getSlotsForDates([slot.date]).some((s) => {
    if (String(s.date || '') !== String(slot.date || '')) return false;
    if (String(s.timeSlot || '') !== String(slot.timeSlot || '')) return false;
    if (String(s.category || '') !== String(slot.category || '')) return false;
    const su = String(s.uocId || '').trim();
    if (resolvedUocId && su && su !== resolvedUocId) return false;
    const sn = normName(s.name);
    return !sn || removeNames.some((n) => normName(n) === sn) || slotLogicalKeyString(s) === slotLogicalKeyString(slot);
  });
  if (!stillThere) return;

  // Fallback name='' (wildcard) solo se non restano posti paralleli con nome diverso.
  const siblings = getSlotsForDates([slot.date]).filter((s) => (
    String(s.date || '') === String(slot.date || '')
    && String(s.timeSlot || '') === String(slot.timeSlot || '')
    && String(s.category || '') === String(slot.category || '')
    && String(s.uocId || resolvedUocId) === resolvedUocId
    && normName(s.name) !== normName(slot.name)
    && normName(s.name) !== normName(slotNameEnsured)
  ));
  if (siblings.length) return;

  await addCalendarModification({
    modificationType: 'remove',
    scope: 'one_off',
    dateSpecific: slot.date,
    dateFrom: slot.date,
    timeSlot: slot.timeSlot,
    category: slot.category,
    uocId: resolvedUocId,
    name: '',
    activityType: slot.activityType || 'clinico',
  }, modOpts);
}

function setWeekRemoveEmptySlotsBtnBusy(btn, busy) {
  if (!btn) return;
  btn.classList.toggle('is-busy', Boolean(busy));
  btn.disabled = Boolean(busy);
  if (busy) btn.setAttribute('aria-busy', 'true');
  else btn.removeAttribute('aria-busy');
}

/** Pin altezza container settimane durante operazioni lunghe per evitare salti di layout. */
function pinWeeksContainerHeight(container) {
  const el = container?.querySelector?.('#weeks-container');
  if (!el) return () => {};
  const h = Math.ceil(el.getBoundingClientRect().height);
  if (h > 0) el.style.minHeight = `${h}px`;
  return () => {
    if (el.isConnected) el.style.minHeight = '';
  };
}

function preserveWindowScrollAround(fn) {
  const scrollY = window.scrollY ?? document.documentElement.scrollTop;
  const scrollX = window.scrollX ?? document.documentElement.scrollLeft;
  const restore = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    });
  };
  return Promise.resolve(fn()).then((v) => {
    restore();
    return v;
  }, (err) => {
    restore();
    throw err;
  });
}

function applyBulkLockIconsInDom(container, weekStart, slotKeys, locked) {
  const weekBlock = container?.querySelector?.(`.week-block[data-week="${weekStart}"]`);
  if (!weekBlock) return;
  const keySet = new Set((slotKeys || []).map((k) => String(k)));
  weekBlock.querySelectorAll('.slot-lock-icon[data-slot-key]').forEach((lockEl) => {
    const k = String(lockEl.dataset.slotKey || '');
    if (!keySet.has(k)) return;
    if (locked) {
      lockEl.classList.remove('unlocked');
      lockEl.classList.add('locked');
      lockEl.textContent = '🔒';
      lockEl.title = 'Sblocca turno';
    } else {
      lockEl.classList.remove('locked');
      lockEl.classList.add('unlocked');
      lockEl.textContent = '🔓';
      lockEl.title = 'Blocca turno';
    }
  });
}

/** Aggiorna disabled/title dei due bottoni bulk lock dopo un'operazione in-place. */
function syncWeekBulkLockButtonsState(container, weekStart, { assignedCount, lockedCount }) {
  const weekBlock = container?.querySelector?.(`.week-block[data-week="${weekStart}"]`);
  if (!weekBlock) return;
  const archived = isWeekLocked(weekStart);
  const noAssigned = !assignedCount;
  const allLocked = assignedCount > 0 && lockedCount >= assignedCount;
  const noneLocked = lockedCount <= 0;
  const archivedTitle = 'Settimana archiviata: sblocca prima il lucchetto ISO';
  const lockBtn = weekBlock.querySelector('.week-bulk-lock-btn--lock');
  const unlockBtn = weekBlock.querySelector('.week-bulk-lock-btn--unlock');
  if (lockBtn) {
    lockBtn.disabled = archived || noAssigned || allLocked;
    lockBtn.title = archived
      ? archivedTitle
      : (allLocked ? 'Tutti gli slot assegnati sono già bloccati' : 'Blocca tutti gli slot assegnati della settimana');
  }
  if (unlockBtn) {
    unlockBtn.disabled = archived || noAssigned || noneLocked;
    unlockBtn.title = archived
      ? archivedTitle
      : (noneLocked ? 'Nessuno slot assegnato è bloccato' : 'Sblocca tutti gli slot assegnati della settimana');
  }
}

async function removeUnassignedSlotsForWeekAndRefresh(container, weekStart, triggerBtn = null) {
  if (!weekStart) return;
  const weekDates = getDaysInWeekRange(weekStart, weekStart).map((d) => d.date);
  const weekSlots = mergePlannerSlotsWithPersisted(weekDates, getSlotsForDates(weekDates));
  const byKey = getState().shiftAssignmentsByKey || {};
  const toRemove = getPlannerUnassignedSlotsToRemove(weekSlots, byKey);
  if (!toRemove.length) {
    alert('Nessuno slot non assegnato in questa settimana.');
    return;
  }
  const ok = await confirmInModal(
    "QUESTA AZIONE CANCELLERA' DAL PIANO DELLA SETTIMANA TUTTI GLI SLOT ANCORA VUOTI. CONFERMI DI VOLER PROCEDERE?",
    {
      title: 'Rimuovi slot non assegnati',
      okLabel: 'Procedi',
      cancelLabel: 'Annulla',
    },
  );
  if (!ok) return;
  plannerBusy = true;
  const btn = triggerBtn?.isConnected
    ? triggerBtn
    : container.querySelector(`.week-remove-empty-slots-btn[data-week="${weekStart}"]`);
  setWeekRemoveEmptySlotsBtnBusy(btn, true);
  const unpin = pinWeeksContainerHeight(container);
  try {
    await withStateBatch(async () => {
      // Due passate: la prima può lasciare posti template (es. SO2) se lo state locale
      // delle remove non era ancora allineato; la seconda ripulisce i residui.
      for (let pass = 0; pass < 2; pass++) {
        const weekSlotsPass = mergePlannerSlotsWithPersisted(weekDates, getSlotsForDates(weekDates));
        const byKeyPass = getState().shiftAssignmentsByKey || {};
        const batch = pass === 0 ? toRemove : getPlannerUnassignedSlotsToRemove(weekSlotsPass, byKeyPass);
        if (!batch.length) break;
        for (const slot of batch) {
          try {
            await removePlannerSlotCore(slot, { skipRefresh: true });
          } catch (err) {
            console.warn('Rimozione slot non assegnato fallita:', slot?.slotKey || slot?.id, err);
          }
        }
      }
      await refreshShiftAssignmentsFromApi();
    }, { notify: false });
    await preserveWindowScrollAround(() => renderShiftPlanner(container));
  } finally {
    unpin();
    setWeekRemoveEmptySlotsBtnBusy(
      btn?.isConnected ? btn : container.querySelector(`.week-remove-empty-slots-btn[data-week="${weekStart}"]`),
      false,
    );
    setTimeout(() => { plannerBusy = false; }, 0);
  }
}

function showAddMissingWeekSlotModal(container, weekStart, onDone) {
  const weekDays = getDaysInWeekRange(weekStart, weekStart);
  if (!weekDays.length) {
    alert('Settimana non valida.');
    return;
  }
  const weekDates = weekDays.map(d => d.date);
  const weekSlots = getSlotsForDates(weekDates);
  const defaultDate = weekDays[0].date;
  const defaultTimeSlot = TIME_SLOTS.MATTINO;
  const defaultCategory = 'terapia_intensiva';
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="max-width:min(560px, calc(100vw - 2rem));">
      <div class="modal-header">
        <h3 style="margin:0;">Aggiungi slot in settimana</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="planner-add-slot-close">×</button>
      </div>
      <div class="modal-body">
        <p style="margin-top:0;color:var(--text-secondary);font-size:0.9rem;">
          Crea uno slot aggiuntivo in Piano Turni senza tornare al Calendario.
        </p>
        <div style="display:flex;flex-wrap:wrap;gap:0.75rem;align-items:flex-end;">
          <div>
            <label for="planner-add-slot-date">Data</label>
            <select id="planner-add-slot-date">
              ${weekDays.map(d => `<option value="${d.date}">${formatDateShort(d.date)} (${String(d.weekdayLabel || '').slice(0, 3)})</option>`).join('')}
            </select>
          </div>
          <div>
            <label for="planner-add-slot-time">Fascia oraria</label>
            <select id="planner-add-slot-time">
              ${Object.values(TIME_SLOTS).map(ts => `<option value="${ts}" ${ts === defaultTimeSlot ? 'selected' : ''}>${TIME_SLOT_LABELS[ts] || ts}</option>`).join('')}
            </select>
          </div>
          <div>
            <label for="planner-add-slot-category">Branca di competenza</label>
            <select id="planner-add-slot-category">
              ${Object.entries(SLOT_CATEGORY_LABELS).map(([k, v]) => `<option value="${k}" ${k === defaultCategory ? 'selected' : ''}>${v}</option>`).join('')}
            </select>
          </div>
          <div style="min-width:100%;">
            <label>UOC</label>
            <div id="planner-add-slot-uoc" style="padding:0.5rem 0.65rem;background:var(--bg-tertiary);border:1px solid var(--border);border-radius:var(--radius);font-size:0.92rem;"></div>
          </div>
          <div style="min-width:100%;">
            <label>Nome nuovo slot</label>
            <input type="text" id="planner-add-slot-name" style="font-weight:600;" placeholder="Nome slot">
            <div id="planner-add-slot-name-hint" style="margin-top:0.35rem;font-size:0.8rem;color:var(--text-secondary);"></div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" id="planner-add-slot-cancel">Annulla</button>
        <button type="button" class="btn btn-primary" id="planner-add-slot-confirm">Aggiungi</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => {
    overlay.remove();
    onDone?.();
  };
  const modalEl = overlay.querySelector('.modal');
  const dateEl = overlay.querySelector('#planner-add-slot-date');
  const timeEl = overlay.querySelector('#planner-add-slot-time');
  const categoryEl = overlay.querySelector('#planner-add-slot-category');
  const uocEl = overlay.querySelector('#planner-add-slot-uoc');
  const nameInputEl = overlay.querySelector('#planner-add-slot-name');
  const nameHintEl = overlay.querySelector('#planner-add-slot-name-hint');
  const stateRef = {
    uocId: CATEGORY_TO_UOC[defaultCategory] || '',
    name: ordinalBaseFallback(defaultCategory),
    suggestedName: ordinalBaseFallback(defaultCategory),
    nameDirty: false,
  };
  nameInputEl?.addEventListener('input', () => {
    stateRef.nameDirty = true;
    stateRef.name = (nameInputEl.value || '').trim();
  });
  nameInputEl?.addEventListener('keydown', (ev) => ev.stopPropagation());
  const recalc = () => {
    const date = dateEl.value || defaultDate;
    const timeSlot = timeEl.value || defaultTimeSlot;
    const category = categoryEl.value || defaultCategory;
    const uocId = CATEGORY_TO_UOC[category] || '';
    const candidates = weekSlots.filter(s =>
      s.date === date
      && s.timeSlot === timeSlot
    );
    const baseHint = weekSlots.find(s =>
      s.timeSlot === timeSlot
      && s.category === category
      && String(s.uocId || '') === String(uocId || '')
    )?.name || '';
    const nextName = computeNextOrdinalNameForDay(candidates, category, baseHint, timeSlot, 'clinico');
    stateRef.uocId = uocId;
    stateRef.suggestedName = nextName;
    if (!stateRef.nameDirty || !stateRef.name) {
      stateRef.name = nextName;
      if (nameInputEl) nameInputEl.value = nextName;
    }
    uocEl.textContent = UOC_LABELS[uocId] || uocId || '—';
    if (nameHintEl) nameHintEl.textContent = `Suggerimento automatico: ${nextName}`;
  };
  recalc();
  dateEl.addEventListener('change', recalc);
  timeEl.addEventListener('change', recalc);
  categoryEl.addEventListener('change', recalc);
  overlay.querySelector('#planner-add-slot-close')?.addEventListener('click', close);
  overlay.querySelector('#planner-add-slot-cancel')?.addEventListener('click', close);
  modalEl?.addEventListener('click', (ev) => ev.stopPropagation());
  modalEl?.addEventListener('mousedown', (ev) => ev.stopPropagation());
  let closeOnOverlayClick = false;
  overlay.addEventListener('mousedown', (ev) => {
    closeOnOverlayClick = ev.target === overlay;
  });
  overlay.addEventListener('click', (ev) => {
    if (ev.target === overlay && closeOnOverlayClick) close();
    closeOnOverlayClick = false;
  });
  overlay.querySelector('#planner-add-slot-confirm')?.addEventListener('click', async () => {
    const date = dateEl.value || defaultDate;
    const timeSlot = timeEl.value || defaultTimeSlot;
    const category = categoryEl.value || defaultCategory;
    const rawName = (nameInputEl?.value || '').trim() || stateRef.suggestedName || stateRef.name || '';
    const activityType = 'clinico';
    const desiredName = canonicalizeSlotName(category, rawName, timeSlot, activityType)
      || rawName
      || ordinalBaseFallback(category);
    const uocId = stateRef.uocId || CATEGORY_TO_UOC[category] || '';
    const existingNames = getSlotsForDates([date])
      .filter((s) => s.date === date && s.timeSlot === timeSlot)
      .map((s) => canonicalizeSlotNameKeepIndex(s.category, s.name, s.timeSlot, s.activityType) || String(s.name || '').trim())
      .filter(Boolean);
    const name = getUniqueSlotName(desiredName, existingNames) || desiredName;
    const ok = await confirmInModal(
      `Aggiungere lo slot "${name}" il ${formatDateShort(date)} (${TIME_SLOT_LABELS[timeSlot] || timeSlot})?`,
      { title: 'Conferma aggiunta slot', okLabel: 'Aggiungi', cancelLabel: 'Annulla' },
    );
    if (!ok) return;
    try {
      await addCalendarModification({
        modificationType: 'add',
        scope: 'one_off',
        dateSpecific: date,
        dateFrom: date,
        timeSlot,
        category,
        uocId,
        name,
        activityType: 'clinico',
      });
      await renderShiftPlanner(container);
      // Dopo render, riapplica evidenziazione sulla cella sorgente (se ancora visibile).
      setPlannerActiveCellFromModal(container, overlay);
      close();
    } catch (err) {
      alert('Errore: ' + (err?.message || err));
    }
  });
  requestAnimationFrame(() => {
    try { nameInputEl?.focus(); } catch (_) {}
  });
}

export async function renderShiftPlanner(container) {
  const showLongReport = (title, body) => {
    const copyTextWithFallback = async (text) => {
      const t = String(text || '');
      try {
        if (navigator?.clipboard?.writeText && window?.isSecureContext) {
          await navigator.clipboard.writeText(t);
          return true;
        }
      } catch (_) {}
      try {
        const ta = document.createElement('textarea');
        ta.value = t;
        ta.setAttribute('readonly', 'true');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        ta.style.pointerEvents = 'none';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        ta.remove();
        return Boolean(ok);
      } catch (_) {
        return false;
      }
    };

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
    const panel = document.createElement('div');
    panel.style.cssText = 'background:var(--bg-primary,#fff);color:var(--text-primary,#111);border:1px solid var(--border,#ccc);border-radius:10px;max-width:1100px;width:min(96vw,1100px);max-height:90vh;display:flex;flex-direction:column;';
    panel.innerHTML = `
      <div style="padding:12px 14px;border-bottom:1px solid var(--border,#ddd);font-weight:700;">${String(title || 'Report')}</div>
      <pre style="margin:0;padding:12px 14px;overflow:auto;white-space:pre-wrap;word-break:break-word;font-size:12px;line-height:1.35;">${String(body || '').replace(/</g, '&lt;')}</pre>
      <div style="padding:10px 14px;border-top:1px solid var(--border,#ddd);display:flex;justify-content:flex-end;gap:8px;">
        <button class="btn btn-secondary" type="button" id="report-copy">Copia</button>
        <button class="btn btn-primary" type="button" id="report-close">Chiudi</button>
      </div>
    `;
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    panel.querySelector('#report-close')?.addEventListener('click', () => overlay.remove());
    panel.querySelector('#report-copy')?.addEventListener('click', async () => {
      const btn = panel.querySelector('#report-copy');
      if (!btn) return;
      const original = btn.textContent;
      const ok = await copyTextWithFallback(body || '');
      btn.textContent = ok ? 'Copiato' : 'Copia fallita';
      setTimeout(() => { btn.textContent = original; }, 1200);
    });
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  };
  // v3: Fix caching issues
  // Non chiamare refresh qui: setState nel refresh innesca subscribe -> render -> loop infinito
  const state = getState();
  const { doctors, shiftPlanWeekFrom, shiftPlanWeekTo } = state;
  const autoLockManualOn = getAutoLockManualAssignments();
  const defaultMon = getMondayOfWeek(new Date().toISOString().slice(0, 10));
  const weekFrom = shiftPlanWeekFrom || defaultMon;
  const weekTo = shiftPlanWeekTo || defaultMon;
  const days = getDaysInWeekRange(weekFrom, weekTo);

  const weekHeaders = [...new Set(days.map(d => getWeekStart(d.date)))].sort().map(ws => ({ weekStart: ws, isoWeek: getISOWeekString(ws) }));
  const canEdit = canEditShifts();
  const canArchiveWeek = canLockWeekArchives();
  const isUserRole = (state.currentUser?.role || '') === 'user';
  const canSeeAutoLockManual = canEdit && !isUserRole;

  const startDate = days[0]?.date;
  const endDate = days[days.length - 1]?.date;

  // Check if we can update in-place
  const weeksContainer = container.querySelector('#weeks-container');
  const isUpdate = !!weeksContainer;

  if (!isUpdate) {
    container.innerHTML = `
      <div class="card shift-planner-page">
      <div class="card-title">Piano turni (base settimanale)</div>
      <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem;">
        ${isUserRole ? 'Solo le settimane archiviate (con lucchetto) sono visibili e stampabili.' : 'Crea i turni dalla settimana X alla settimana Y. Gli slot sono generati dal Calendario.'}
      </p>
      <div class="planner-toolbar">
        <div class="planner-toolbar-filters">
          <div>
            <label>Intervallo settimane</label>
            ${weekRangeSelectorHTML('planner-weeks', weekFrom, weekTo)}
          </div>
          ${canSeeAutoLockManual ? `<div class="planner-toolbar-auto-lock">
            <button type="button" class="btn ${autoLockManualOn ? 'btn-primary' : 'btn-secondary'}" id="btn-auto-lock-manual"
              aria-pressed="${autoLockManualOn ? 'true' : 'false'}"
              title="${autoLockManualOn ? 'Disattiva: le assegnazioni da tendina o trascinamento restano sbloccate (il solver può modificarle).' : 'Attiva: ogni assegnazione manuale viene salvata con lucchetto (il solver non la sovrascrive).'}">
              ${autoLockManualOn ? '🔒 Lucchetto auto (manuale): attivo' : '🔓 Lucchetto auto (manuale): disattivo'}
            </button>
          </div>` : ''}
        </div>
        <div class="planner-toolbar-actions planner-toolbar-actions--generate">
          ${canEdit ? `
            <button class="btn btn-primary" id="btn-generate-draft">Genera tutto</button>
            <button class="btn btn-secondary" id="btn-generate-weekend">Genera Weekend</button>
            <button class="btn btn-secondary" id="btn-generate-notti">Genera Notti</button>
            <button class="btn btn-secondary" id="btn-generate-infrasettimanali">Genera Infrasettimanali</button>
            <button class="btn btn-secondary" id="btn-generate-reperibilita">Genera Reperibilità</button>
            <button class="btn btn-secondary" id="btn-clear-draft">Pulisci bozza</button>
          ` : ''}
        </div>
        <div class="planner-toolbar-actions planner-toolbar-actions--exports">
          <button type="button" class="btn btn-pdf" id="btn-export-pdf">${PDF_DOWNLOAD_ICON_HTML}<span class="btn-pdf-label">Esporta PDF</span></button>
          ${canArchiveWeek ? `<button type="button" class="btn btn-success" id="btn-email-pdf"><span aria-hidden="true">✉</span> Invia tramite e-mail</button>` : ''}
          ${canEdit ? `<button type="button" class="btn btn-secondary" id="btn-import-excel"><span aria-hidden="true">📥</span> Importa Excel/PDF</button>` : ''}
          <button type="button" class="btn btn-excel" id="btn-export-excel"><span aria-hidden="true">📊</span> Esporta Excel</button>
          ${canEdit ? '<input type="file" id="input-import-excel" accept=".xlsx,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf" style="display:none" />' : ''}
          <button type="button" class="btn btn-calendar" id="btn-export-ics"><span aria-hidden="true">🗓</span> Scarica calendario (.ics)</button>
        </div>
      </div>
      <div id="weeks-container">
        <!-- Week blocks will be rendered here -->
      </div>
    </div>
  `;

  // Debounce cambio settimana per evitare blocchi con re-render pesanti
  let weekChangeTimeout = null;
  setupWeekRangeSelector(container, 'planner-weeks', (val) => {
    if (weekChangeTimeout) clearTimeout(weekChangeTimeout);
    weekChangeTimeout = setTimeout(() => {
      weekChangeTimeout = null;
      if (val?.weekFrom && val?.weekTo) setShiftPlanWeekRange(val.weekFrom, val.weekTo);
    }, 100);
  });

  async function runPhaseOrAll(phase) {
    const btn = container.querySelector(phase ? `#btn-generate-${phase}` : '#btn-generate-draft');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = 'Generazione in corso...';
    btn.setAttribute('aria-busy', 'true');
    await new Promise(r => setTimeout(r, 150));
    try {
      const st = getState();
      const wf = st.shiftPlanWeekFrom || getMondayOfWeek(new Date().toISOString().slice(0, 10));
      const wt = st.shiftPlanWeekTo || wf;
      const d = getDaysInWeekRange(wf, wt);
      const dateList = d.map(x => x.date);
      if (phase === 'infrasettimanali' || phase === 'reperibilita') {
        const boundary = getNightRestBoundaryDate(wf);
        if (boundary && !dateList.includes(boundary)) dateList.unshift(boundary);
      }
      const allSlotsForPhase = getOfficialSlotsForDates(dateList);
      const slots = phase
        ? allSlotsForPhase.filter(s => getSlotPhase(s) === phase)
        : allSlotsForPhase;
      if (slots.length === 0) {
        alert('Configura prima il Calendario con i template settimanali. Il Piano Turni mostra solo gli slot definiti lì.');
        return;
      }

      const phaseLabel = (p) => (
        p === 'weekend' ? 'Weekend'
        : p === 'notti' ? 'Notti'
        : p === 'infrasettimanali' ? 'Infrasettimanali'
        : 'Reperibilità'
      );

      const applyPhaseResult = async (result, phase) => {
        const currentState = getState();
        const existingByKey = currentState.shiftAssignmentsByKey || {};
        const lockedByKey = currentState.lockedAssignmentsByKey || {};
        const solverAssignments = result?.assignments || {};
        const autoUnlocked = new Set(result?.autoUnlockedSlotIds || []);

        const assignments = {};
        const lockedStatus = {};
        for (const slot of slots) {
          const key = slot.slotKey || slot.id;
          const isLocked = Boolean(lockedByKey[key]) && !autoUnlocked.has(key) && !autoUnlocked.has(slot.id);
          if (isLocked) {
            assignments[slot.id] = existingByKey[key];
            lockedStatus[slot.id] = true;
          } else {
            const assigned = solverAssignments[slot.id] || solverAssignments[slot.slotKey];
            if (assigned) assignments[slot.id] = assigned;
          }
        }
        await setShiftPlanBulk(slots, assignments, lockedStatus, result?.autoUnlockedSlotIds || [], { phase });
        // Ripopola shiftSlots da API: setShiftPlanBulk azzera shiftSlots; senza righe DB
        // mergeNightDatesFromExistingAssignments non risolve chiavi -lgk-* e il solver infra
        // non riceve assignedNightDatesByDoctor → 409 regola 9 su MdD/CP (spesso BT).
        await refreshShiftAssignmentsFromApi();
      };

      const buildPhaseSummary = (result, currentPhase) => {
        const overtimeSlotIds = result?.overtimeSlotIds || [];
        const levelingDiagnostics = result?.levelingDiagnostics || [];
        const overtimeCount = overtimeSlotIds.length;
        const report = formatLevelingReport(levelingDiagnostics, overtimeCount);
        const hasLevelingIssues = overtimeCount > 0 || /⚠|Errore|INFEASIBLE|rilassamento diagnostico|slot non assegnati/i.test(report);
        const phaseTitle = hasLevelingIssues
          ? `Fase ${phaseLabel(currentPhase)}: verifica consigliata`
          : `Fase ${phaseLabel(currentPhase)}: esito positivo`;
        let phaseReport = report;
        if (!phaseReport && overtimeCount > 0) {
          phaseReport = `${overtimeCount} slot non assegnati. Assegnali manualmente se necessario.`;
        } else if (!phaseReport) {
          phaseReport = 'Nessuna criticità rilevata.';
        }
        return {
          phaseTitle,
          phaseReport,
          hasLevelingIssues,
        };
      };

      if (phase) {
        const existingByKey = st.shiftAssignmentsByKey || {};
        const result = await autoAssignSlotsForPhase(slots, existingByKey, phase);
        await applyPhaseResult(result, phase);
        const summary = buildPhaseSummary(result, phase);
        showLongReport(summary.phaseTitle, summary.phaseReport);
      } else {
        // Genera tutto in 4 step senza popup intermedi: unico riepilogo finale.
        const sequence = ['weekend', 'notti', 'infrasettimanali', 'reperibilita'];
        const phaseSummaries = [];
        for (const p of sequence) {
          btn.textContent = `Generazione in corso... (${phaseLabel(p)})`;
          const currentByKey = getState().shiftAssignmentsByKey || {};
          const result = await autoAssignSlotsForPhase(slots, currentByKey, p);
          await applyPhaseResult(result, p);
          phaseSummaries.push({
            phase: p,
            ...buildPhaseSummary(result, p),
          });
        }
        const hasIssues = phaseSummaries.some(s => s.hasLevelingIssues);
        const finalTitle = hasIssues
          ? 'Generazione completa: verifica consigliata'
          : 'Generazione completa: esito positivo';
        const finalReport = phaseSummaries.map((s) => {
          return `Fase ${phaseLabel(s.phase)}\n${s.phaseReport}`;
        }).join('\n\n--------------------\n\n');
        showLongReport(finalTitle, finalReport);
      }
    } catch (e) {
      alert('Errore: ' + e.message);
    } finally {
      btn.disabled = false;
      btn.textContent = phase ? `Genera ${phase === 'weekend' ? 'Weekend' : phase === 'notti' ? 'Notti' : phase === 'infrasettimanali' ? 'Infrasettimanali' : 'Reperibilità'}` : 'Genera tutto';
      btn.removeAttribute('aria-busy');
    }
  }

  container.querySelector('#btn-generate-draft')?.addEventListener('click', () => runPhaseOrAll(null));
  container.querySelector('#btn-generate-weekend')?.addEventListener('click', () => runPhaseOrAll('weekend'));
  container.querySelector('#btn-generate-notti')?.addEventListener('click', () => runPhaseOrAll('notti'));
  container.querySelector('#btn-generate-infrasettimanali')?.addEventListener('click', () => runPhaseOrAll('infrasettimanali'));
  container.querySelector('#btn-generate-reperibilita')?.addEventListener('click', () => runPhaseOrAll('reperibilita'));

  container.querySelector('#btn-export-pdf').addEventListener('click', async () => {
    const btn = container.querySelector('#btn-export-pdf');
    const label = btn.querySelector('.btn-pdf-label');
    btn.disabled = true;
    if (label) label.textContent = 'Generazione...';
    const result = await exportShiftPlanToPDF();
    btn.disabled = false;
    if (label) label.textContent = 'Esporta PDF';
    if (!result.ok) {
      alert('Errore durante l\'esportazione PDF: ' + (result.error || 'Errore sconosciuto'));
    }
  });

  container.querySelector('#btn-export-excel')?.addEventListener('click', async () => {
    const btn = container.querySelector('#btn-export-excel');
    if (!btn) return;
    btn.disabled = true;
    const prevText = btn.textContent;
    btn.textContent = 'Generazione Excel...';
    try {
      const result = await exportShiftPlanToExcel({ root: container });
      if (!result.ok) {
        alert('Errore durante l\'esportazione Excel: ' + (result.error || 'Errore sconosciuto'));
      }
    } catch (err) {
      alert('Errore durante l\'esportazione Excel: ' + (err?.message || err));
    } finally {
      btn.disabled = false;
      btn.textContent = prevText || '📊 Esporta Excel';
    }
  });

  container.querySelector('#btn-import-excel')?.addEventListener('click', () => {
    container.querySelector('#input-import-excel')?.click();
  });

  container.querySelector('#input-import-excel')?.addEventListener('change', async (e) => {
    const input = e.target;
    const file = input?.files?.[0];
    if (!file) return;
    const importBtn = container.querySelector('#btn-import-excel');
    const prevText = importBtn?.textContent || 'Importa Excel/PDF';
    if (importBtn) {
      importBtn.disabled = true;
      importBtn.textContent = 'Import in corso...';
    }
    try {
      const result = await apiImportShiftPlanXlsx(file);
      setShiftImportIssues(result?.issues || []);
      await refreshShiftAssignmentsFromApi({ refreshCalendarModifications: true });
      renderShiftPlanner(container);
      const s = result?.summary || {};
      const issues = Array.isArray(result?.issues) ? result.issues : [];
      const doctorsById = new Map((getState().doctors || []).map((d) => [String(d.id), String(d.sigla || '').trim()]));
      const details = issues.slice(0, 120).map((x) => {
        const row = x?.source_row ? `riga ${x.source_row}` : 'riga n/d';
        const where = [formatDateItDash(x?.date), x?.time_slot, x?.category].filter(Boolean).join(' | ');
        const docSigla = doctorsById.get(String(x?.doctor_id || x?.doctorId || '').trim()) || '';
        const docTxt = docSigla ? ` [${docSigla}]` : '';
        return `- [${x?.reason_code || 'IMPORT_ERROR'}] ${row}${where ? ` (${where})` : ''}${docTxt}: ${x?.reason_message || 'Errore'}`;
      }).join('\n');
      showLongReport(
        'Report import turni',
        [
          ...describeImportedDocument(result?.document),
          `Importati: ${s.imported || 0}`,
          `Scartati: ${s.discarded || 0}`,
          `Slot creati: ${s.createdSlots || 0}`,
          `Assegnazioni sovrascritte nello stesso import: ${s.overwrittenInImport || 0}`,
          `Settimane archiviate ignorate: ${s.skippedLockedWeeks || 0}`,
          `Celle lucchettate ignorate: ${s.skippedLockedCells || 0}`,
          '',
          details || 'Nessun errore.',
        ].join('\n')
      );
    } catch (err) {
      alert(`Errore import: ${err?.message || err}`);
    } finally {
      if (importBtn) {
        importBtn.disabled = false;
        importBtn.textContent = prevText;
      }
      if (input) input.value = '';
    }
  });

  container.querySelector('#btn-email-pdf')?.addEventListener('click', async () => {
    const btn = container.querySelector('#btn-email-pdf');
    if (!btn) return;
    const st = getState();
    const wf = st.shiftPlanWeekFrom || getMondayOfWeek(new Date().toISOString().slice(0, 10));
    const wt = st.shiftPlanWeekTo || wf;
    const rangeDays = getDaysInWeekRange(wf, wt).map(d => d.date).sort();
    const rangeStart = rangeDays[0] || wf;
    const rangeEnd = rangeDays[rangeDays.length - 1] || wt;
    const weekStarts = [...new Set(getDaysInWeekRange(wf, wt).map(d => getWeekStart(d.date)))];
    const unlocked = weekStarts.filter(ws => !isWeekLocked(ws));
    if (unlocked.length > 0) {
      alert('Invio non possibile: alcune settimane non sono state bloccate');
      return;
    }
    const okSendAll = await confirmInModal(
      'Conferma invio e-mail',
      `Confermi l'invio a tutti tramite mail del PDF dal ${formatDateShort(rangeStart)} al ${formatDateShort(rangeEnd)}?`,
      'Invia e-mail',
      'Annulla'
    );
    if (!okSendAll) return;
    btn.disabled = true;
    const prevText = btn.textContent;
    btn.textContent = 'Invio in corso...';
    try {
      const users = await apiGetUsers();
      const doctorsById = new Map((st.doctors || []).map((d) => [d.id, d]));
      // Un invio per utente: il PDF e' personalizzato sul suo medico e il backend
      // lo recapita a tutti i suoi indirizzi abilitati al piano turni.
      const recipients = [];
      for (const u of (users || [])) {
        if (!hasShiftPdfRecipient(u)) continue;
        const did = String(u?.doctorId || '').trim();
        recipients.push({
          userId: String(u.id),
          label: String(u?.username || u?.email || u.id),
          doctorId: did && doctorsById.has(did) ? did : null,
        });
      }
      if (recipients.length === 0) {
        throw new Error('Nessun utente con e-mail configurata');
      }

      const pdfCache = new Map();
      const getPdfForDoctor = async (doctorIdOrNull) => {
        const key = doctorIdOrNull || '__generic__';
        if (pdfCache.has(key)) return pdfCache.get(key);
        const pdfResult = await exportShiftPlanToPDF({
          download: false,
          returnBlob: true,
          highlightDoctorId: doctorIdOrNull || null,
        });
        if (!pdfResult.ok || !pdfResult.blob) {
          throw new Error(pdfResult.error || 'Impossibile generare PDF');
        }
        pdfCache.set(key, pdfResult);
        return pdfResult;
      };

      const failed = [];
      let sent = 0;
      for (const item of recipients) {
        const doctorIdForRecipient = item.doctorId;
        try {
          const pdfResult = await getPdfForDoctor(doctorIdForRecipient);
          let icsBlob = null;
          let icsFilename = null;
          if (doctorIdForRecipient) {
            const icsResult = await apiDownloadShiftCalendarIcsForDoctor(
              doctorIdForRecipient,
              pdfResult.rangeStart,
              pdfResult.rangeEnd,
            );
            icsBlob = icsResult?.blob || null;
            icsFilename = icsResult?.filename || null;
          }
          await apiSendShiftPlanPdfEmail({
            dateFrom: pdfResult.rangeStart,
            dateTo: pdfResult.rangeEnd,
            pdfBlob: pdfResult.blob,
            filename: pdfResult.filename,
            recipientUserId: item.userId,
            icsBlob,
            icsFilename,
          });
          sent += 1;
        } catch (errOne) {
          failed.push(`${item.label}: ${errOne?.message || errOne}`);
        }
      }

      if (failed.length > 0) {
        throw new Error(`Invio completato parzialmente (${sent}/${recipients.length}).\n${failed.join('\n')}`);
      }
      alert(`Invio dei turni via e-mail completato con successo (${sent} destinatari).`);
    } catch (err) {
      alert('Errore durante l\'invio e-mail: ' + (err?.message || err));
    } finally {
      btn.disabled = false;
      btn.textContent = prevText || 'Invia tramite e-mail';
    }
  });

  container.querySelector('#btn-export-ics')?.addEventListener('click', async () => {
    const btn = container.querySelector('#btn-export-ics');
    if (!btn) return;
    const st = getState();
    const doctorId = String(st?.currentUser?.doctorId || '').trim();
    if (!doctorId) {
      alert("Non c'è un calendario personale associato al tuo account.");
      return;
    }
    const wf = st.shiftPlanWeekFrom || getMondayOfWeek(new Date().toISOString().slice(0, 10));
    const wt = st.shiftPlanWeekTo || wf;
    btn.disabled = true;
    const prevText = btn.textContent;
    btn.textContent = 'Generazione calendario...';
    try {
      const days = getDaysInWeekRange(wf, wt).map(d => d.date).sort();
      const dateFrom = days[0];
      const dateTo = days[days.length - 1];
      const { blob, filename } = await apiDownloadMyShiftCalendarIcs(dateFrom, dateTo);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `turni-${dateFrom}-${dateTo}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore durante export calendario: ' + (err?.message || err));
    } finally {
      btn.disabled = false;
      btn.textContent = prevText || '🗓 Scarica calendario (.ics)';
    }
  });

  // Event Delegation for dynamic content (slot-assign-select)
  if (!container.dataset.plannerChangeBound) {
    container.dataset.plannerChangeBound = '1';
    container.addEventListener('change', async e => {
      if (e.target.classList.contains('slot-assign-select')) {
        const select = e.target;
        if (select.dataset.plannerSuppressChange === '1') return;
        if (select.disabled || !canEditShifts()) return;
        const innerForActive = select.closest?.('.slot-cell-inner.view-table') || select.closest?.('.slot-cell-inner') || null;
        if (innerForActive) setPlannerActiveCell(container, innerForActive);
        const slotKey = select.dataset.slotKey;
        const prevAssignment = getAssignmentByKey(slotKey) || '';
        let doctorId = select.value || null;
        if (doctorId && doctorId.startsWith('str:')) doctorId = doctorId.slice(4);
        if (doctorId === '__separator__') {
          revertPlannerSlotSelect(select, prevAssignment);
          return;
        }
        
        const st = getState();
        const wf = st.shiftPlanWeekFrom || weekFrom;
        const wt = st.shiftPlanWeekTo || weekTo;
        const allSlots = buildPlannerSlotsForValidation(wf, wt, slotKey?.slice(0, 10));
        let slot = allSlots.find(s => (s.slotKey || s.id) === slotKey);
        if (!slot) {
          slot = buildSyntheticSlotFromKey(slotKey);
          if (slot) allSlots = [...allSlots, slot];
        }
        if (!slot) {
          alert('Slot non trovato. Aggiorna la pagina.');
          revertPlannerSlotSelect(select, prevAssignment);
          return;
        }
        let usedNightSmontoOverride = false;
        let needAbsenceDesiderataClear = false;
        if (doctorId === '__remove_slot__') {
          // Importante: non lasciare il select "appeso" su __remove_slot__ mentre
          // parte un render asincrono; in alcuni browser questo causa click successivi
          // che colpiscono opzioni/righe sbagliate.
          try {
            const inner = select.closest?.('.slot-cell-inner.view-table');
            if (inner) closeTableSlotAssignSelect(inner);
          } catch (_) {}
          revertPlannerSlotSelect(select, prevAssignment);
          // Dopo la selezione di un'option, alcuni browser emettono un click "fantasma":
          // se nel frattempo il DOM cambia (render), il click può colpire un'altra riga.
          tableSkipNextCellClick = true;
          try {
            setTimeout(() => {
              try { if (document.activeElement === select) select.blur(); } catch (_) {}
            }, 0);
          } catch (_) {}

          const ok = await confirmInModal(
            `Rimuovere lo slot "${slot.name || getCompactSlotLabel(slot)}" del ${formatDateShort(slot.date)} (${TIME_SLOT_LABELS[slot.timeSlot] || slot.timeSlot})?`,
            { title: 'Rimuovi slot', okLabel: 'Rimuovi', cancelLabel: 'Annulla' },
          );
          if (!ok) {
            return;
          }
          try {
            await removePlannerSlotAndRefresh(container, slot);
          } catch (err) {
            alert('Errore: ' + (err?.message || err));
          }
          return;
        }
        if (doctorId) {
          const current = st.shiftAssignmentsByKey || {};
          const simulated = { ...current, [slotKey]: doctorId };
          const [yr] = slot.date.split('-').map(Number);
          const maxDays = getFestiviCountInWeek(getWeekStart(slot.date), yr) > 0 ? 4 : 5;
          const validationOpts = {
            slots: allSlots,
            assignmentsByKey: simulated,
            allowManualOverride: true,
            allowOverrideZeroPercent: true,
            maxDaysPerWeek: maxDays,
          };
          // Override manuali con conferma. Accumula i flag così da poter gestire più
          // incompatibilità sovrapposte (es. notte adiacente + smonto) sullo stesso slot.
          const overrideOpts = { ...validationOpts };
          let res = canAssignDoctorToSlot(doctorId, slot, overrideOpts);
          let overrideGuard = 0;
          while (!res.ok && res.reasonCode && overrideGuard++ < 6) {
            if (res.reasonCode === 'direzione_not_enabled') {
              if (!await confirmDirezioneManualOverride(doctorId, slot)) {
                revertPlannerSlotSelect(select, prevAssignment);
                return;
              }
              overrideOpts.allowDirezioneManualOverride = true;
            } else if (res.reasonCode === 'absence_desiderata_override') {
              if (!await confirmAbsenceDesiderataManualOverride(doctorId, slot)) {
                revertPlannerSlotSelect(select, prevAssignment);
                return;
              }
              overrideOpts.allowAbsenceDesiderataManualOverride = true;
              needAbsenceDesiderataClear = true;
            } else if (res.reasonCode === 'night_consecutive') {
              if (!await confirmNightConsecutiveManualOverride()) {
                revertPlannerSlotSelect(select, prevAssignment);
                return;
              }
              overrideOpts.allowNightConsecutiveManualOverride = true;
              overrideOpts.allowNightRestSmontoManualOverride = true;
              usedNightSmontoOverride = true;
            } else if (res.reasonCode === 'night_rest_smonto') {
              if (!await confirmNightRestSmontoManualOverride()) {
                revertPlannerSlotSelect(select, prevAssignment);
                return;
              }
              overrideOpts.allowNightRestSmontoManualOverride = true;
              usedNightSmontoOverride = true;
            } else {
              break;
            }
            res = canAssignDoctorToSlot(doctorId, slot, overrideOpts);
          }
          if (!res.ok) {
            alert(res.reason);
            revertPlannerSlotSelect(select, prevAssignment);
            return;
          }
          // Popup ripartizione 0%: solo assi con barra (Direzione usa solo il flag, vedi sopra).
          const doctor = st.doctors.find(d => d.id === doctorId);
          const distKey = zeroPercentWarningDistKey(slot);
          const pct = distKey != null && doctor ? doctor.distribuzione?.[distKey] : undefined;
          const isTI = slot.category === 'terapia_intensiva';
          const isTINightOrWeekend = isTI && (slot.timeSlot === 'notte' || ['sab', 'dom'].includes(slot.weekday));
          if (pct === 0 && !isTINightOrWeekend) {
            const areaLabel = zeroPercentWarningAreaLabel(slot, distKey);
            const ok = await confirmInModal(
              `Attenzione: stai assegnando un medico con 0% nella ripartizione di ${areaLabel}. Continuare?`,
              { title: 'Ripartizione 0%', okLabel: 'Continua', cancelLabel: 'Annulla' },
            );
            if (!ok) {
              revertPlannerSlotSelect(select, prevAssignment);
              return;
            }
          }
        }
        try {
          const isTableGrid = select.closest('.shift-table-grid[data-view-mode="table"]');
          const doctorForPersist = getState().doctors.find(d => d.id === doctorId);
          const direzionePersistOverride = isDirezioneSlotKind(slot) && doctorForPersist && !doctorForPersist.abilitaDirezione;
          const persistAssign = {};
          if (direzionePersistOverride) persistAssign.allowDirezioneManualOverride = true;
          if (usedNightSmontoOverride) persistAssign.allowNightRestSmontoManualOverride = true;
          if (doctorId) {
            if (needAbsenceDesiderataClear) {
              await clearAbsenceDesiderataConflictForSlot(doctorId, slot);
            }
            await assignDoctorToSlotByKey(slotKey, doctorId, slot, getAutoLockManualAssignments(), persistAssign);
          }
          else await unassignSlotByKey(slotKey, slot);
          if (isTableGrid) await syncPlannerTableSlotSiblingsFromPrimary(slot, slotKey, doctorId, allSlots);
          refreshPlannerWeekStats(container);
        } catch (err) {
          alert('Errore: ' + err.message);
          revertPlannerSlotSelect(select, prevAssignment);
          // La UI non deve restare più "avanti" del DB: rileggi lo stato reale.
          await refreshShiftAssignmentsFromApi();
        }
      }
    });
  }

  if (canEdit && !container.dataset.dragDropSetup) {
    container.dataset.dragDropSetup = '1';
    setupDragAndDrop(container);
  }

  const autoLockBtn = container.querySelector('#btn-auto-lock-manual');
  if (autoLockBtn && !autoLockBtn.dataset.bound) {
    autoLockBtn.dataset.bound = '1';
    autoLockBtn.addEventListener('click', () => {
      const next = !getAutoLockManualAssignments();
      setAutoLockManualAssignments(next);
      autoLockBtn.classList.toggle('btn-primary', next);
      autoLockBtn.classList.toggle('btn-secondary', !next);
      autoLockBtn.setAttribute('aria-pressed', next ? 'true' : 'false');
      autoLockBtn.title = next
        ? 'Disattiva: le assegnazioni da tendina o trascinamento restano sbloccate (il solver può modificarle).'
        : 'Attiva: ogni assegnazione manuale viene salvata con lucchetto (il solver non la sovrascrive).';
      autoLockBtn.textContent = next ? '🔒 Lucchetto auto (manuale): attivo' : '🔓 Lucchetto auto (manuale): disattivo';
    });
  }

  // Vista tabella: click su cella o sigla apre select estesa; sulla sigla, pressione ~0,1 s abilita il drag (vedi tableDragHoldBound).
  if (!container.dataset.tableViewClickBound) {
    container.dataset.tableViewClickBound = '1';
    container.addEventListener('click', (e) => {
      if (plannerBusy) return;
      if (tableSkipNextCellClick) {
        tableSkipNextCellClick = false;
        return;
      }
      const cellInner = e.target.closest('.slot-cell-inner.view-table');
      if (!cellInner) {
        container.querySelectorAll('.slot-cell-inner.view-table[data-open="1"]').forEach(el => {
          closeTableSlotAssignSelect(el);
        });
        clearPlannerActiveCell(container);
        return;
      }
      if (e.target.closest('.slot-lock-icon, .slot-assign-select')) return;
      const select = cellInner.querySelector('.slot-assign-select');
      if (!select || select.disabled) return;
      container.querySelectorAll('.slot-cell-inner.view-table[data-open="1"]').forEach(el => {
        if (el !== cellInner) closeTableSlotAssignSelect(el);
      });
      setPlannerActiveCell(container, cellInner);
      openTableSlotAssignSelect(cellInner, select);
    });
    container.addEventListener('change', (e) => {
      if (!e.target.classList || !e.target.classList.contains('slot-assign-select')) return;
      const inner = e.target.closest('.slot-cell-inner.view-table');
      if (inner) closeTableSlotAssignSelect(inner);
    });
    // Stesso valore (es. slot vuoto + "Nessuna assegnazione"): change non parte; chiudi al click sull'option.
    container.addEventListener('click', (e) => {
      const opt = e.target;
      if (opt.tagName !== 'OPTION') return;
      const sel = opt.parentElement;
      if (!sel?.classList.contains('slot-assign-select')) return;
      const innerTable = sel.closest('.slot-cell-inner.view-table');
      if (innerTable?.getAttribute('data-open') === '1') {
        setTimeout(() => closeTableSlotAssignSelect(innerTable), 0);
        return;
      }
      setTimeout(() => {
        try {
          if (document.activeElement === sel) sel.blur();
        } catch (_) {}
      }, 0);
    });
    container.addEventListener('focusout', (e) => {
      const inner = e.target.closest?.('.slot-cell-inner.view-table');
      if (!inner) return;
      setTimeout(() => {
        if (!inner.contains(document.activeElement)) closeTableSlotAssignSelect(inner);
      }, 0);
    });
  }

  if (!container.dataset.tableDragHoldBound) {
    container.dataset.tableDragHoldBound = '1';
    const clearTableDragArmTimer = () => {
      if (tableDragArmTimer != null) {
        clearTimeout(tableDragArmTimer);
        tableDragArmTimer = null;
      }
    };
    const disarmTableDragBadge = () => {
      clearTableDragArmTimer();
      tableDragPointerDown = false;
      if (tableDragPointerBadge) {
        tableDragPointerBadge.classList.remove('table-drag-armed');
        const inner = tableDragPointerBadge.closest('.slot-cell-inner.view-table');
        const sel = inner?.querySelector('.slot-assign-select');
        const sk = tableDragPointerBadge.dataset.slotId;
        if (sel && !sel.disabled && sk && !isPlannerSlotKeyDnDLocked(sk)) {
          tableDragPointerBadge.draggable = false;
        }
        tableDragPointerBadge = null;
      }
      tableDragArmed = false;
    };
    container.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      const badge = e.target.closest('.slot-cell-inner.view-table .badge-draggable.grid-sigla');
      if (!badge) return;
      const innerPd = badge.closest('.slot-cell-inner.view-table');
      const selPd = innerPd?.querySelector('.slot-assign-select');
      if (!selPd || selPd.disabled) return;
      const skPd = badge.dataset.slotId;
      if (skPd && isPlannerSlotKeyDnDLocked(skPd)) return;
      disarmTableDragBadge();
      tableDragStarted = false;
      tableDragPointerDown = true;
      tableDragPointerBadge = badge;
      badge.draggable = false;
      tableDragArmTimer = setTimeout(() => {
        tableDragArmTimer = null;
        if (!tableDragPointerDown || tableDragPointerBadge !== badge) return;
        tableDragArmed = true;
        badge.classList.add('table-drag-armed');
        badge.draggable = true;
      }, TABLE_DRAG_ARM_MS);
    }, true);
    const onDocTableDragPointerEnd = () => {
      if (!tableDragPointerBadge) return;
      if (!tableDragStarted) disarmTableDragBadge();
    };
    const onDocTableDragPointerCancel = () => {
      if (tableDragStarted) return;
      disarmTableDragBadge();
    };
    document.addEventListener('pointerup', onDocTableDragPointerEnd, true);
    document.addEventListener('pointercancel', onDocTableDragPointerCancel, true);
  }

  } // End of if (!isUpdate)

  // Listener lucchetto slot e settimana: sempre attivo (anche in update) per evitare perdita dopo re-render
  if (!container.dataset.lockClickAttached) {
    container.dataset.lockClickAttached = '1';
    container.addEventListener('click', async e => {
      const weekLockBtn = e.target.closest('.week-lock-btn');
      if (weekLockBtn) {
        if (!canEditShifts() || !canLockWeekArchives()) return;
        e.stopPropagation();
        e.preventDefault();
        const weekStart = weekLockBtn.dataset.week;
        if (!weekStart) return;
        const locked = isWeekLocked(weekStart);
        try {
          if (locked) {
            await unlockWeek(weekStart);
            renderShiftPlanner(container);
            return;
          }

          const lockResp = await lockWeek(weekStart);
          renderShiftPlanner(container);

          const note = lockResp?.shiftChangeNotification;
          const suggested = Boolean(note?.suggested) && Array.isArray(note?.affectedDoctorIds) && note.affectedDoctorIds.length > 0;
          if (!suggested) return;

          const ok = await confirmInModal(
            'Invio una mail di notifica ai medici interessati dai cambi?',
            {
              title: 'Notifica',
              okLabel: 'Conferma Invio',
              cancelLabel: 'Non inviare',
            },
          );
          if (!ok) return;

          const dateFrom = String(note?.dateFrom || '').slice(0, 10);
          const dateTo = String(note?.dateTo || '').slice(0, 10);
          if (!dateFrom || !dateTo) return;

          const fmtIt = (iso) => {
            const s = String(iso || '').slice(0, 10);
            if (s.length === 10 && s[4] === '-' && s[7] === '-') return `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}`;
            return s || '-';
          };
          const emailSubject = `I turni del periodo ${fmtIt(dateFrom)} - ${fmtIt(dateTo)} sono stati modificati.`;
          const emailBody = 'In allegato la nuova versione, controlla attentamente i cambiamenti effettuati e comunica eventuali incongruenze.';

          const st = getState();
          const doctorsById = new Map((st.doctors || []).map((d) => [String(d.id), d]));
          const users = await apiGetUsers();
          const userIdByDoctorId = new Map();
          const hasEmailByDoctorId = new Map();
          for (const u of (users || [])) {
            const did = String(u?.doctorId || u?.doctor_id || '').trim();
            if (!did) continue;
            const uid = String(u?.id || u?.userId || '').trim();
            if (uid && !userIdByDoctorId.has(did)) userIdByDoctorId.set(did, uid);
            if (!hasEmailByDoctorId.has(did)) hasEmailByDoctorId.set(did, hasShiftPdfRecipient(u));
          }

          const changesByDoctor = note?.changesByDoctor || {};

          const failed = [];
          let sent = 0;
          let pushed = 0;
          for (const doctorId of note.affectedDoctorIds) {
            const did = String(doctorId || '').trim();
            if (!did || !doctorsById.has(did)) continue;
            const uid = userIdByDoctorId.get(did);
            if (!uid || !hasEmailByDoctorId.get(did)) {
              failed.push(`${doctorsById.get(did)?.sigla || did}: email mancante`);
              continue;
            }
            const ch = changesByDoctor[String(did)] || {};
            const addedSlotIds = Array.isArray(ch.addedSlotIds) ? ch.addedSlotIds : [];
            const removedSlotIds = Array.isArray(ch.removedSlotIds) ? ch.removedSlotIds : [];
            try {
              const pdfResult = await exportShiftPlanToPDF({
                download: false,
                returnBlob: true,
                highlightDoctorId: did,
                weekFrom: dateFrom,
                weekTo: dateTo,
                changedSlotKeys: [...new Set([...addedSlotIds, ...removedSlotIds])],
              });
              if (!pdfResult.ok || !pdfResult.blob) throw new Error(pdfResult.error || 'Impossibile generare PDF');

              const icsDelta = await apiDownloadShiftCalendarDeltaIcsForDoctor({
                doctorId: did,
                dateFrom,
                dateTo,
                addedSlotIds,
                removedSlotIds,
              });

              await apiSendShiftPlanPdfEmail({
                dateFrom,
                dateTo,
                pdfBlob: pdfResult.blob,
                filename: pdfResult.filename,
                recipientUserId: uid,
                icsBlob: icsDelta?.blob || null,
                icsFilename: icsDelta?.filename || null,
                subject: emailSubject,
                messageText: emailBody,
              });
              sent += 1;

              if (uid) {
                try {
                  await apiAdminShiftPlanNotifyDelta({
                    userIds: [uid],
                    dateFrom,
                    dateTo,
                  });
                  pushed += 1;
                } catch (_) {}
              }
            } catch (errOne) {
              failed.push(`${doctorsById.get(did)?.sigla || did}: ${errOne?.message || errOne}`);
            }
          }

          if (failed.length) {
            alert(`Invio completato parzialmente (mail: ${sent}, push: ${pushed}).\n${failed.join('\n')}`);
          } else {
            alert(`Invio completato con successo (mail: ${sent}, push: ${pushed}).`);
          }
        } catch (err) {
          alert('Errore: ' + err.message);
        }
        return;
      }
      const absentChip = e.target.closest('.grid-absent-chip');
      if (absentChip && !absentChip.classList.contains('is-readonly')) {
        e.stopPropagation();
        e.preventDefault();
        if (!canManagePlannerAbsenceRow()) return;
        const date = absentChip.dataset.date || '';
        const doctorId = absentChip.dataset.doctorId || '';
        if (!date || !doctorId) return;
        const doctor = (getState().doctors || []).find(d => String(d.id) === String(doctorId));
        if (!doctor) return;
        const itemKeys = (absentChip.dataset.itemKeys || '').split(',').filter(Boolean);
        showCellActionSheet(null, doctor.id, [date], { allowExtend: false, focusItemKeys: itemKeys });
        return;
      }
      const absentAddBtn = e.target.closest('.grid-absent-add');
      if (absentAddBtn) {
        e.stopPropagation();
        e.preventDefault();
        if (!canManagePlannerAbsenceRow()) return;
        const date = absentAddBtn.dataset.date || '';
        if (!date) return;
        showCellActionSheet(null, null, [date], {
          allowExtend: false,
          doctorPicker: plannerAbsencePickerDoctors(date),
        });
        return;
      }
      const weekHighlightBtn = e.target.closest('.week-highlight-toggle');
      if (weekHighlightBtn) {
        e.stopPropagation();
        e.preventDefault();
        const weekStart = weekHighlightBtn.dataset.week;
        const doctorId = weekHighlightBtn.dataset.doctorId;
        if (!weekStart || !doctorId) return;
        const set = getOrCreateWeekHighlightSet(weekStart);
        if (set.has(doctorId)) set.delete(doctorId);
        else set.add(doctorId);
        removeWeekHighlightSetIfEmpty(weekStart);
        renderShiftPlanner(container);
        return;
      }
      const weekBulkBtn = e.target.closest('.week-bulk-lock-btn');
      if (weekBulkBtn) {
        if (!canEditShifts()) return;
        e.stopPropagation();
        e.preventDefault();
        const weekStart = weekBulkBtn.dataset.week;
        if (!weekStart) return;
        if (isWeekLocked(weekStart)) {
          alert('Settimana archiviata: sblocca prima il lucchetto ISO.');
          return;
        }
        const targetLocked = weekBulkBtn.dataset.targetLocked === '1';
        const weekDates = getDaysInWeekRange(weekStart, weekStart).map(d => d.date);
        const weekSlots = mergePlannerSlotsWithPersisted(weekDates, getSlotsForDates(weekDates));
        const byKey = getState().shiftAssignmentsByKey || {};
        const targets = getPlannerCanonicalAssignedTargets(weekSlots, byKey);
        if (!targets.length) {
          alert('Nessuno slot assegnato nella settimana selezionata.');
          return;
        }
        const weekBlock = weekBulkBtn.closest('.week-block');
        const bulkBtns = weekBlock
          ? Array.from(weekBlock.querySelectorAll('.week-bulk-lock-btn'))
          : [weekBulkBtn];
        bulkBtns.forEach((btn) => {
          btn.disabled = true;
          btn.classList.add('is-busy');
          btn.setAttribute('aria-busy', 'true');
        });
        const unpin = pinWeeksContainerHeight(container);
        const failed = [];
        const updatedKeys = [];
        try {
          await withStateBatch(async () => {
            for (const t of targets) {
              try {
                const dW = getState().doctors.find(x => String(x.id) === String(t.doctorId));
                const wExtra = {};
                if (t.slot && isDirezioneSlotKind(t.slot) && dW && !dW.abilitaDirezione) {
                  wExtra.allowDirezioneManualOverride = true;
                }
                if (t.slot && t.doctorId) {
                  const [yrW] = t.slot.date.split('-').map(Number);
                  const maxW = getFestiviCountInWeek(getWeekStart(t.slot.date), yrW) > 0 ? 4 : 5;
                  if (needsNightRestSmontoManualSlot(t.doctorId, t.slot, weekSlots, byKey, maxW)) {
                    wExtra.allowNightRestSmontoManualOverride = true;
                  }
                }
                await assignDoctorToSlotByKey(t.key, t.doctorId, t.slot, targetLocked, wExtra);
                updatedKeys.push(t.key);
              } catch (err) {
                failed.push({ key: t.key, err });
              }
            }
          }, { notify: false });
          // Aggiorna lucchetti in-place: niente re-render completo (evita salti).
          applyBulkLockIconsInDom(container, weekStart, updatedKeys, targetLocked);
          const lockedByKey = getState().lockedAssignmentsByKey || {};
          const lockedCount = targets.filter((t) => Boolean(lockedByKey[t.key])).length;
          syncWeekBulkLockButtonsState(container, weekStart, {
            assignedCount: targets.length,
            lockedCount,
          });
          if (failed.length) {
            alert(`Operazione completata con errori: ${failed.length} slot non aggiornati.`);
          }
        } catch (err) {
          alert('Errore: ' + (err?.message || err));
        } finally {
          unpin();
          bulkBtns.forEach((btn) => {
            if (!btn.isConnected) return;
            btn.classList.remove('is-busy');
            btn.removeAttribute('aria-busy');
          });
          const lockedByKey = getState().lockedAssignmentsByKey || {};
          syncWeekBulkLockButtonsState(container, weekStart, {
            assignedCount: targets.length,
            lockedCount: targets.filter((t) => Boolean(lockedByKey[t.key])).length,
          });
        }
        return;
      }
      const weekAddSlotBtn = e.target.closest('.week-add-slot-btn');
      if (weekAddSlotBtn) {
        if (!canEditShifts()) return;
        e.stopPropagation();
        e.preventDefault();
        const weekStart = weekAddSlotBtn.dataset.week;
        if (!weekStart) return;
        if (isWeekLocked(weekStart)) {
          alert('Settimana archiviata: aggiunta slot non consentita.');
          return;
        }
        showAddMissingWeekSlotModal(container, weekStart);
        return;
      }
      const weekRemoveEmptyBtn = e.target.closest('.week-remove-empty-slots-btn');
      if (weekRemoveEmptyBtn) {
        if (!canEditShifts()) return;
        e.stopPropagation();
        e.preventDefault();
        const weekStart = weekRemoveEmptyBtn.dataset.week;
        if (!weekStart) return;
        if (isWeekLocked(weekStart)) {
          alert('Settimana archiviata: rimozione slot non consentita.');
          return;
        }
        if (weekRemoveEmptyBtn.classList.contains('is-busy') || weekRemoveEmptyBtn.disabled) return;
        try {
          await removeUnassignedSlotsForWeekAndRefresh(container, weekStart, weekRemoveEmptyBtn);
        } catch (err) {
          alert('Errore: ' + (err?.message || err));
        }
        return;
      }
      const addSlotCell = e.target.closest('.grid-cell-na.add-slot-cell');
      if (addSlotCell) {
        if (!canEditShifts()) return;
        // Ignora il click se è in corso un aggiornamento (es. rimozione slot precedente):
        // evita che il target finisca su una cella diversa e apra il pop-up "crea slot".
        if (plannerBusy) { e.stopPropagation(); e.preventDefault(); return; }
        e.stopPropagation();
        e.preventDefault();
        setPlannerActiveCell(container, addSlotCell);
        // Serve a ri-applicare l'evidenziazione dopo render (quando la cella non esiste più come nodo).
        try {
          const d = addSlotCell.dataset.date || '';
          const ts = addSlotCell.dataset.timeSlot || '';
          const cat = addSlotCell.dataset.category || '';
          const uoc = addSlotCell.dataset.uocId || '';
          const nm = addSlotCell.dataset.name || '';
          container.dataset.plannerActiveCellSrc = `add-cell:${d}|${ts}|${cat}|${uoc}|${nm}`;
        } catch (_) {}
        const date = addSlotCell.dataset.date;
        const timeSlot = addSlotCell.dataset.timeSlot;
        const category = addSlotCell.dataset.category;
        const uocId = addSlotCell.dataset.uocId || CATEGORY_TO_UOC[category] || '';
        const rawBaseName = (addSlotCell.dataset.name || '').trim();
        const activityType = addSlotCell.dataset.activityType || 'clinico';
        const baseName = ensurePlannerSlotName(category, rawBaseName, timeSlot, activityType);
        const weekStart = getWeekStart(date);
        if (isWeekLocked(weekStart)) {
          alert('Settimana archiviata: aggiunta slot non consentita.');
          return;
        }
        const ok = await confirmInModal(
          `Aggiungere uno slot "${baseName}" il ${formatDateShort(date)} (${TIME_SLOT_LABELS[timeSlot] || timeSlot})?`,
          { title: 'Aggiungi slot', okLabel: 'Aggiungi', cancelLabel: 'Annulla' },
        );
        if (!ok) { clearPlannerActiveCell(container); return; }
        plannerBusy = true;
        try {
          const existingNames = getSlotsForDates([date])
            .filter((s) => s.date === date && s.timeSlot === timeSlot)
            .map((s) => canonicalizeSlotNameKeepIndex(s.category, s.name, s.timeSlot, s.activityType) || String(s.name || '').trim())
            .filter(Boolean);
          const uniqueName = getUniqueSlotName(baseName, existingNames) || baseName;
          await addCalendarModification({
            modificationType: 'add',
            scope: 'one_off',
            dateSpecific: date,
            dateFrom: date,
            timeSlot,
            category,
            uocId,
            name: uniqueName,
            activityType,
          });
          await renderShiftPlanner(container);
        } catch (err) {
          alert('Errore: ' + (err?.message || err));
          clearPlannerActiveCell(container);
        } finally {
          setTimeout(() => { plannerBusy = false; }, 0);
        }
        return;
      }
      const lockEl = e.target.closest('.slot-lock-icon');
      if (!lockEl) return;
      if (!canEdit) return;
      e.stopPropagation();
      e.preventDefault();
      const slotKey = lockEl.dataset.slotKey;
      // Settimana archiviata: sbloccare il singolo turno non cambia nulla (la cella resta in
      // sola lettura) e al render successivo l'icona tornerebbe indietro da sola.
      if (isWeekLocked(getWeekStart(String(slotKey || '').slice(0, 10)))) return;
      const doctorId = getAssignmentByKey(slotKey);
      if (!doctorId) return;
      const isLocked = lockEl.classList.contains('locked');
      const newLocked = !isLocked;
      if (newLocked) {
        lockEl.classList.remove('unlocked');
        lockEl.classList.add('locked');
        lockEl.textContent = '🔒';
        lockEl.title = 'Sblocca turno';
      } else {
        lockEl.classList.remove('locked');
        lockEl.classList.add('unlocked');
        lockEl.textContent = '🔓';
        lockEl.title = 'Blocca turno';
      }
      const st = getState();
      const wf = st.shiftPlanWeekFrom || weekFrom;
      const wt = st.shiftPlanWeekTo || weekTo;
      const allSlots = buildPlannerSlotsForValidation(wf, wt, slotKey?.slice(0, 10));
      let slot = allSlots.find(s => (s.slotKey || s.id) === slotKey);
      if (!slot) {
        slot = buildSyntheticSlotFromKey(slotKey);
        if (slot) allSlots = [...allSlots, slot];
      }
      if (!slot) {
        alert('Slot non trovato. Aggiorna la pagina.');
        if (newLocked) { lockEl.classList.remove('locked'); lockEl.classList.add('unlocked'); lockEl.textContent = '🔓'; lockEl.title = 'Blocca turno'; }
        else { lockEl.classList.remove('unlocked'); lockEl.classList.add('locked'); lockEl.textContent = '🔒'; lockEl.title = 'Sblocca turno'; }
        return;
      }
      try {
        const dL = st.doctors.find(x => String(x.id) === String(doctorId));
        const [yrL] = slot.date.split('-').map(Number);
        const maxL = getFestiviCountInWeek(getWeekStart(slot.date), yrL) > 0 ? 4 : 5;
        const lockExtra = {};
        if (isDirezioneSlotKind(slot) && dL && !dL.abilitaDirezione) lockExtra.allowDirezioneManualOverride = true;
        if (needsNightRestSmontoManualSlot(doctorId, slot, allSlots, st.shiftAssignmentsByKey || {}, maxL)) {
          lockExtra.allowNightRestSmontoManualOverride = true;
        }
        await assignDoctorToSlotByKey(slotKey, doctorId, slot, newLocked, lockExtra);
        const grid = lockEl.closest('.shift-table-grid[data-view-mode="table"]');
        if (grid && doctorId) {
          for (const sib of plannerTableSlotsSameLabelRow(slot, allSlots)) {
            const k = sib.slotKey || sib.id;
            if (k === slotKey) continue;
            const dAsn = getAssignmentByKey(k);
            if (dAsn && String(dAsn) === String(doctorId)) {
              const [yrS] = sib.date.split('-').map(Number);
              const maxS = getFestiviCountInWeek(getWeekStart(sib.date), yrS) > 0 ? 4 : 5;
              const sibExtra = {};
              if (isDirezioneSlotKind(sib) && dL && !dL.abilitaDirezione) sibExtra.allowDirezioneManualOverride = true;
              if (needsNightRestSmontoManualSlot(dAsn, sib, allSlots, st.shiftAssignmentsByKey || {}, maxS)) {
                sibExtra.allowNightRestSmontoManualOverride = true;
              }
              await assignDoctorToSlotByKey(k, dAsn, sib, newLocked, sibExtra);
            }
          }
        }
      } catch (err) {
        alert('Errore aggiornamento blocco: ' + err.message);
        if (newLocked) { lockEl.classList.remove('locked'); lockEl.classList.add('unlocked'); lockEl.textContent = '🔓'; lockEl.title = 'Blocca turno'; }
        else { lockEl.classList.remove('unlocked'); lockEl.classList.add('locked'); lockEl.textContent = '🔒'; lockEl.title = 'Sblocca turno'; }
      }
    });
  }

  // Sincronizza selettore intervallo settimane con lo state (quando isUpdate: valori potrebbero essere cambiati)
  if (isUpdate) {
    const fromEl = container.querySelector('#planner-weeks-from');
    const toEl = container.querySelector('#planner-weeks-to');
    const yearFromEl = container.querySelector('#planner-weeks-year-from');
    const yearToEl = container.querySelector('#planner-weeks-year-to');
    if (fromEl && toEl && weekFrom && weekTo) {
      if (fromEl.value !== weekFrom) fromEl.value = weekFrom;
      if (toEl.value !== weekTo) toEl.value = weekTo;
      if (yearFromEl) {
        const yFrom = getISOWeekYear(weekFrom);
        if (parseInt(yearFromEl.value, 10) !== yFrom) {
          yearFromEl.value = yFrom;
          refreshWeekSelectOptions(fromEl, yearFromEl);
          fromEl.value = weekFrom;
        }
      }
      if (yearToEl) {
        const yTo = getISOWeekYear(weekTo);
        if (parseInt(yearToEl.value, 10) !== yTo) {
          yearToEl.value = yTo;
          refreshWeekSelectOptions(toEl, yearToEl);
          toEl.value = weekTo;
        }
      }
    }
  }

  // Re-bind Clear Draft button to use current dates
  const clearBtn = container.querySelector('#btn-clear-draft');
  if (clearBtn) {
    const newBtn = clearBtn.cloneNode(true);
    clearBtn.parentNode.replaceChild(newBtn, clearBtn);
    newBtn.addEventListener('click', async () => {
      if (!startDate || !endDate) return;
      newBtn.textContent = 'Pulizia in corso...';
      newBtn.disabled = true;
      try {
        await clearShiftPlanForDateRange(startDate, endDate);
        // Aggiorna la UI (incluse le card "Stato Ore / Giorni") dopo la pulizia.
        // Le card sono calcolate nel setTimeout di renderShiftPlanner: senza un re-render restano stale.
        try { await refreshShiftAssignmentsFromApi(); } catch (_) {}
        await renderShiftPlanner(container);
      } finally {
        const btn = container.querySelector('#btn-clear-draft');
        if (btn) { btn.textContent = 'Pulisci bozza'; btn.disabled = false; }
      }
    });
  }

  // Calcoli pesanti in setTimeout per non bloccare l'UI
  const renderGen = ++plannerRenderGeneration;
  // Le chiavi sono numeri di generazione: non destrutturarle (`[g]` prova a iterare il number).
  for (const g of [...plannerRenderWaiters.keys()]) {
    if (g !== renderGen) resolvePlannerRenderWaiter(g);
  }
  const paintDone = waitForPlannerRenderGeneration(renderGen);
  setTimeout(() => {
    try {
    if (!container.isConnected) return;
    if (renderGen !== plannerRenderGeneration) return;
    const slotsFromCalendar = buildPlannerSlotsForValidation(weekFrom, weekTo);
    const assignmentsByKey = getState().shiftAssignmentsByKey || {};
    const lockedAssignmentsByKey = getState().lockedAssignmentsByKey || {};
    // Colonna "Saldo Bim": solo per chi pianifica. Il medico ha il proprio saldo in Home e Carichi.
    const showBimesterBalance = Boolean(canSeeWorkloadBimesterBalances());

    const weeksContainer = container.querySelector('#weeks-container');
    if (!weeksContainer) return;

    const allWeekStarts = [...new Set(days.map(d => getWeekStart(d.date)))];
    const expectedWeekStarts = (isUserRole ? allWeekStarts.filter(ws => isWeekLocked(ws)) : allWeekStarts).sort();
    const displayedWeekStarts = Array.from(weeksContainer.querySelectorAll('.week-block')).map(b => b.dataset.week).filter(Boolean).sort();
    const weekRangeChanged = JSON.stringify(expectedWeekStarts) !== JSON.stringify(displayedWeekStarts);
    if (weekRangeChanged) weeksContainer.innerHTML = '';

    // Group days by week
    const daysByWeek = {};
    days.forEach(d => {
      const ws = getWeekStart(d.date);
      if (!daysByWeek[ws]) daysByWeek[ws] = [];
      daysByWeek[ws].push(d);
    });

    // Render each week (per user: solo settimane archiviate/bloccate)
    const weeksToRender = isUserRole ? Object.keys(daysByWeek).filter(ws => isWeekLocked(ws)) : Object.keys(daysByWeek);

    if (isUserRole && weeksToRender.length === 0) {
      const msg = document.createElement('p');
      msg.style.cssText = 'color: var(--text-secondary); font-size: 0.9rem; margin: 1rem 0;';
      msg.textContent = 'Nessuna settimana archiviata nell\'intervallo selezionato. Solo le settimane bloccate (con lucchetto) sono visibili.';
      if (!weeksContainer.querySelector('p')) weeksContainer.appendChild(msg);
    } else {
      const emptyMsg = weeksContainer.querySelector('p');
      if (emptyMsg) emptyMsg.remove();
    }
    weeksToRender.sort().forEach(weekStart => {
      const weekDays = daysByWeek[weekStart];
      const isoWeek = getISOWeekString(weekStart);
      const weekSlots = slotsFromCalendar.filter(s => weekDays.some(d => d.date === s.date));

      const dupBlocks = weeksContainer.querySelectorAll(`.week-block[data-week="${weekStart}"]`);
      dupBlocks.forEach((b, i) => { if (i > 0) b.remove(); });
      let weekBlock = dupBlocks[0] || weeksContainer.querySelector(`.week-block[data-week="${weekStart}"]`);
      const weekLocked = isWeekLocked(weekStart);
      const weekBulk = getWeekBulkLockState(weekStart, weekSlots, assignmentsByKey, lockedAssignmentsByKey);
      const showWeekBulkBtn = canEdit && !isUserRole;
      if (!weekBlock) {
        weekBlock = document.createElement('div');
        weekBlock.className = 'week-block card';
        weekBlock.dataset.week = weekStart;
        weeksContainer.appendChild(weekBlock);

        weekBlock.innerHTML = `
          <div class="week-header">
            <span class="week-title-iso">${isoWeek}</span>
            <span class="week-title-range">(${formatDateShort(weekDays[0].date)} – ${formatDateShort(weekDays[weekDays.length-1].date)})</span>
            ${weekLocked ? '<span class="badge badge-ti week-archived-badge">Archiviata</span>' : ''}
            ${getWeekHeaderActionsHTML({ weekStart, weekLocked, canEdit, canArchiveWeek, showWeekBulkBtn, weekBulk })}
          </div>
          ${getWeekBalanceBarHTML(weekStart)}
          <div class="week-layout">
            <div class="week-shifts">
              ${renderWeekShiftsContainerHTML(weekStart)}
            </div>
            <div class="week-stats">
              <div class="week-stats-title" title="Ore e giorni escludono reperibilità; CO/Permesso non accreditano ore su sab/dom e festivi infrasettimanali">Stato Ore / Giorni ${isoWeek}</div>
              <div class="week-stats-content">
                <table class="sidebar-status-table">
                  <thead><tr><th>Medico</th><th style="text-align:center;">Ore</th><th style="text-align:center;" title="Giorni lavorati nella settimana">Gg</th>${showBimesterBalance ? '<th style="text-align:center;" title="Saldo del bimestre cumulato fino alla fine di questa settimana: coperte + riporto − dovute. Ore sopra, slot equivalenti sotto.">Saldo Bim</th>' : ''}</tr></thead>
                  <tbody id="stats-${weekStart}"></tbody>
                </table>
              </div>
            </div>
          </div>
        `;
      } else {
        const header = weekBlock.querySelector('.week-header');
        if (header) {
          header.innerHTML = `
            <span class="week-title-iso">${isoWeek}</span>
            <span class="week-title-range">(${formatDateShort(weekDays[0].date)} – ${formatDateShort(weekDays[weekDays.length-1].date)})</span>
            ${weekLocked ? '<span class="badge badge-ti week-archived-badge">Archiviata</span>' : ''}
            ${getWeekHeaderActionsHTML({ weekStart, weekLocked, canEdit, canArchiveWeek, showWeekBulkBtn, weekBulk })}
          `;
          const existingBar = weekBlock.querySelector(':scope > .week-balance-bar');
          if (existingBar) {
            existingBar.outerHTML = getWeekBalanceBarHTML(weekStart);
          } else {
            header.insertAdjacentHTML('afterend', getWeekBalanceBarHTML(weekStart));
          }
        }
      }
      weekBlock.dataset.weekLocked = weekLocked ? '1' : '0';

      renderWeekTableBody(weekBlock, weekStart, weekDays, slotsFromCalendar, assignmentsByKey);
    });

    refreshPlannerWeekStats(container, { weekFrom, weekTo, doctors, days });

    // Remove weeks that are no longer in range
    const currentWeekStarts = new Set(Object.keys(daysByWeek));
    Array.from(weeksContainer.querySelectorAll('.week-block')).forEach(block => {
      if (!currentWeekStarts.has(block.dataset.week)) {
        plannerWeekHighlightedDoctors.delete(String(block.dataset.week || ''));
        block.remove();
      }
    });

    // Calcola offset sticky reale dell'header settimana per allineare la riga Data/Giorno/Slot
    syncWeekStickyOffsets(container);
    syncWeekShiftsScrollFade(container);
    requestAnimationFrame(() => {
      syncWeekStickyOffsets(container);
      syncWeekShiftsScrollFade(container);
    });

    // Riapplica evidenziazione "cella attiva" dopo un render completo (es. dopo aggiunta slot con "+").
    try {
      setPlannerActiveCellFromModal(container, container);
    } catch (_) {}
    if (!container.dataset.weekStickyResizeBound) {
      container.dataset.weekStickyResizeBound = '1';
      window.addEventListener('resize', () => {
        if (!container.isConnected) return;
        syncWeekStickyOffsets(container);
        syncWeekShiftsScrollFade(container);
      }, { passive: true });
    }
    if (typeof ResizeObserver !== 'undefined') {
      if (!weekStickyResizeObserver) {
        weekStickyResizeObserver = new ResizeObserver(() => {
          if (!container.isConnected) return;
          syncWeekStickyOffsets(container);
        });
      }
      container.querySelectorAll('.week-block .week-header').forEach((header) => {
        if (header.dataset.weekStickyObserved === '1') return;
        header.dataset.weekStickyObserved = '1';
        weekStickyResizeObserver.observe(header);
      });
    }

    } finally {
      resolvePlannerRenderWaiter(renderGen);
    }
  }, 0);
  return paintDone;
}

/**
 * Target settimanale medico = (debito orario settimanale ÷ 5) × giorni feriali lun–ven non festivi.
 * Giallo: sotto target − 2h; verde: target ± 2h; arancione: sopra target + 2h.
 */
function getDoctorWeekStatus(doctors, weeklyBreakdown, year, days) {
  const weekStarts = [...new Set(days.map(d => getWeekStart(d.date)))].sort();
  const weeks = weekStarts.map(ws => ({ weekStart: ws, isoWeek: getISOWeekString(ws) }));
  const byDoctor = {};
  for (const doc of doctors) {
    byDoctor[doc.id] = {};
    for (const w of weeks) {
      const h = weeklyBreakdown[w.weekStart]?.[doc.id];
      const tot = h?.total ?? 0;
      const festiviCount = getFestiviCountInWeek(w.weekStart, year);
      const workingDays = Math.max(0, 5 - festiviCount);
      const hoursPerDay = getHoursPerDayForDoctorOnDate(doc.id, w.weekStart);
      const targetWeekly = workingDays * hoursPerDay;
      const bandMin = targetWeekly - WEEK_STATS_HOURS_TOLERANCE;
      const bandMax = targetWeekly + WEEK_STATS_HOURS_TOLERANCE;

      let status = 'empty';
      if (tot > 0) {
        const totR = Math.round(tot * 10) / 10;
        if (totR < bandMin - 1e-6) {
          status = 'sotto';
        } else if (totR > bandMax + 1e-6) {
          status = 'sopra';
        } else {
          status = 'ok';
        }
      }
      const daysWorked = h?.days ?? 0;
      const weekEnd = (() => {
        const d = new Date(w.weekStart + 'T12:00:00');
        d.setDate(d.getDate() + 6);
        return d.toISOString().slice(0, 10);
      })();
      const absenceCreditedHours = getCreditedAbsenceHoursForRange(doc.id, w.weekStart, weekEnd);
      byDoctor[doc.id][w.weekStart] = {
        hours: tot,
        absenceCreditedHours,
        workingDays,
        hoursPerDay,
        targetWeekly,
        bandMin,
        bandMax,
        status,
        days: daysWorked,
      };
    }
  }
  return { weeks, byDoctor };
}

/**
 * Data a cui fermare il conto del bimestre per una settimana: la domenica, troncata alla fine del
 * bimestre del lunedì. Così la settimana a cavallo chiude il bimestre a cui appartiene e non
 * sconfina in quello dopo, che ricomincia da zero.
 */
function bimesterCutoffForWeek(weekStart) {
  const range = getBimesterForDate(weekStart);
  if (!range) return null;
  const sunday = addCalendarDays(weekStart, 6);
  return sunday > range.end ? range.end : sunday;
}

/** Badge del saldo bimestrale: ore sopra, slot equivalenti sotto (1 slot ≈ 6h). */
function bimesterBalanceBadgeHtml(entry, cutoff, rangeLabel, doctor) {
  if (isExcludedFromWorkloadBalance(doctor)) {
    return '<span class="week-badge week-badge-empty" title="Direttore: solo turni Direzione, fuori dal bilanciamento del monte orario">—</span>';
  }
  if (!entry || !(entry.dovute > 0)) {
    return '<span class="week-badge week-badge-empty" title="Nessun monte orario da coprire in questo bimestre">—</span>';
  }
  const h = Math.round(entry.saldo * 10) / 10;
  const slots = Math.round((entry.saldo / HOURS_PER_SLOT) * 10) / 10;
  const cls = h < -0.05 ? 'week-badge-sotto' : 'week-badge-ok';
  const hLabel = `${h > 0 ? '+' : ''}${h.toFixed(1)}h`;
  const slotLabel = `${slots > 0 ? '+' : ''}${slots.toFixed(1)} slot`;
  const esito = h < -0.05
    ? `${Math.abs(h).toFixed(1)}h da recuperare`
    : (h > 0.05 ? `${h.toFixed(1)}h di credito` : 'in pari');
  const title = `Bimestre ${rangeLabel} al ${formatDateShort(cutoff)}: coperte ${entry.coperte.toFixed(1)}h`
    + `${Math.abs(entry.saldoApertura) > 0.05 ? ` + riporto ${entry.saldoApertura > 0 ? '+' : ''}${entry.saldoApertura.toFixed(1)}h` : ''}`
    + ` − dovute ${entry.dovute.toFixed(1)}h → ${esito}. 1 slot ≈ ${HOURS_PER_SLOT}h.`;
  return `<span class="week-badge ${cls}" title="${title}"><span class="week-hours-main">${hLabel}</span><span class="week-hours-sub">${slotLabel}</span></span>`;
}

function buildWeekStatsRowsHtml(doctors, doctorWeekStatus, weekStart, bimesterCell = null) {
  const fmt = (n) => Number(n).toFixed(1);
  return doctors.map((doc) => {
    const s = doctorWeekStatus.byDoctor[doc.id]?.[weekStart];
    if (!s) return '';
    const badgeClass = `week-badge-${s.status || 'empty'}`;
    const agLabel = `${fmt(s.absenceCreditedHours || 0)}h AG`;
    const label = `<span class="week-hours-main">${fmt(s.hours)}h</span><span class="week-hours-ag">(${agLabel})</span>`;
    const title = `Target ${fmt(s.targetWeekly)}h (${s.workingDays} gg feriali × ${fmt(s.hoursPerDay)}h); verde ${fmt(s.bandMin)}–${fmt(s.bandMax)}h`;
    const daysVal = s.days ?? 0;
    const highlighted = (plannerWeekHighlightedDoctors.get(String(weekStart || '')) || new Set()).has(String(doc.id));
    return `<tr>
          <td><button type="button" class="week-highlight-toggle${highlighted ? ' active' : ''}" data-week="${weekStart}" data-doctor-id="${doc.id}" title="${highlighted ? 'Disattiva evidenziazione slot' : 'Attiva evidenziazione slot'}" aria-pressed="${highlighted ? 'true' : 'false'}">💡</button> <strong>${doc.sigla}</strong> ${doc.cognome}</td>
          <td style="text-align:center;"><span class="week-badge ${badgeClass}" title="${title}">${label}</span></td>
          <td style="text-align:center;">${daysVal}</td>
          ${bimesterCell ? `<td style="text-align:center;">${bimesterCell(doc)}</td>` : ''}
        </tr>`;
  }).join('');
}

/** Aggiorna solo le card Stato Ore/Giorni (senza re-render completo della griglia). */
function refreshPlannerWeekStats(container, opts = {}) {
  const state = getState();
  const defaultMon = getMondayOfWeek(new Date().toISOString().slice(0, 10));
  const weekFrom = opts.weekFrom ?? state.shiftPlanWeekFrom ?? defaultMon;
  const weekTo = opts.weekTo ?? state.shiftPlanWeekTo ?? defaultMon;
  const doctors = opts.doctors ?? state.doctors ?? [];
  const days = opts.days ?? getDaysInWeekRange(weekFrom, weekTo);
  const isUserRole = (state.currentUser?.role || '') === 'user';
  const weeksContainer = container.querySelector('#weeks-container');
  if (!weeksContainer) return;

  const slotsForBreakdown = buildPlannerSlotsForValidation(weekFrom, weekTo);
  const weeklyBreakdown = computeWeeklyBreakdownFromSlots(slotsForBreakdown);
  const doctorWeekStatus = getDoctorWeekStatus(
    doctors,
    weeklyBreakdown,
    new Date(weekFrom).getFullYear(),
    days,
  );

  const daysByWeek = {};
  days.forEach((d) => {
    const ws = getWeekStart(d.date);
    if (!daysByWeek[ws]) daysByWeek[ws] = [];
    daysByWeek[ws].push(d);
  });
  const weeksToRender = isUserRole
    ? Object.keys(daysByWeek).filter((ws) => isWeekLocked(ws))
    : Object.keys(daysByWeek);

  // Saldo bimestrale cumulato a fine di ogni settimana mostrata: una passata per bimestre, qui,
  // così il ricalcolo a ogni assegnazione resta dello stesso ordine del resto della card.
  const showBimesterBalance = Boolean(canSeeWorkloadBimesterBalances());
  const cutoffByWeek = {};
  if (showBimesterBalance) {
    for (const ws of weeksToRender) {
      const cutoff = bimesterCutoffForWeek(ws);
      if (cutoff) cutoffByWeek[ws] = cutoff;
    }
  }
  // Il Direttore fa solo turni Direzione: niente saldo bimestrale, ne' qui ne' nel solver.
  const bimesterBalances = showBimesterBalance
    ? computeBimesterBalancesAtCutoffs(Object.values(cutoffByWeek), doctorsForWorkloadBalance(doctors))
    : {};

  weeksToRender.sort().forEach((weekStart) => {
    const blocks = weeksContainer.querySelectorAll(`.week-block[data-week="${weekStart}"]`);
    blocks.forEach((b, i) => { if (i > 0) b.remove(); });
    const weekBlock = blocks[0];
    if (!weekBlock) return;
    const statsBody = statsTbodyForWeek(weekBlock, weekStart);
    if (!statsBody) return;
    const cutoff = cutoffByWeek[weekStart];
    const balance = cutoff ? bimesterBalances[cutoff] : null;
    const bimesterCell = balance
      ? (doc) => bimesterBalanceBadgeHtml(balance.byDoctor[doc.id], cutoff, balance.range.label, doc)
      : null;
    statsBody.innerHTML = buildWeekStatsRowsHtml(doctors, doctorWeekStatus, weekStart, bimesterCell);
  });
}

/** Restituisce l'HTML interno di `.week-shifts` per la settimana (vista a tabella). */
function renderWeekShiftsContainerHTML(weekStart) {
  return `
      <table class="shift-table shift-table-grid" data-view-mode="table">
        <thead>
          <tr id="thead-grid-${weekStart}"></tr>
        </thead>
        <tbody id="tbody-grid-${weekStart}"></tbody>
        ${canSeePlannerAbsenceRow() ? `<tfoot><tr class="grid-absent-row" id="tfoot-absent-${weekStart}"></tr></tfoot>` : ''}
      </table>
    `;
}

const WEEKDAY_LETTER_UI = { lun: 'L', mar: 'M', mer: 'M', gio: 'G', ven: 'V', sab: 'S', dom: 'D' };

/** Voci della legenda colori, nell'ordine in cui i chip compaiono nelle celle. */
const PLANNER_ABSENT_LEGEND_ITEMS = [
  { kind: 'absence', label: 'assenza', title: 'Congedo, permesso, malattia, rischio radiologico: tutto il giorno' },
  { kind: 'lp', label: 'libera prof.', title: 'Libera professione: le lettere indicano le fasce impegnate', managerOnly: true },
  { kind: 'formazione', label: 'formazione', title: 'Formazione: F da solo è la giornata intera' },
  { kind: 'desiderata', label: 'desiderata', title: 'Desiderata: le lettere sono le fasce rifiutate' },
];

/** Legenda visibile nella testata di riga: i colori valgono più della frase nel tooltip. */
function plannerAbsentRowLegendHtml({ canManage }) {
  const items = PLANNER_ABSENT_LEGEND_ITEMS
    .filter(it => canManage || !it.managerOnly)
    .map(it => `<span class="grid-absent-legend-item" title="${escapeAttr(it.title)}">`
      + `<i class="grid-absent-legend-dot grid-absent-chip-${it.kind}" aria-hidden="true"></i>${escapeAttr(it.label)}</span>`)
    .join('');
  return `<span class="grid-absent-legend">${items}</span>`;
}

/** Testo del tooltip: quello che la legenda a colori non può dire (fasce e comandi). */
function plannerAbsentRowLegend({ canManage }) {
  return [
    'Medici non disponibili, per intero o su alcune fasce.',
    'Sigla senza lettere = intera giornata; le lettere indicano le fasce (M mattino, P pomeriggio, Pr prolungato, N notte).',
    canManage ? 'Clic su un chip per modificarlo, «+» per aggiungerne uno.' : '',
  ].filter(Boolean).join(' ');
}

/** Medici ordinati per cognome: stesso criterio della card Stato Ore / Giorni. */
function plannerAbsenceRowDoctors() {
  return [...(getState().doctors || [])].sort((a, b) =>
    String(a?.cognome || '').localeCompare(String(b?.cognome || ''), 'it', { sensitivity: 'base' }));
}

/**
 * Riga «Assenze/Desiderata» nel piè della griglia: una casella per giorno con le sigle dei medici
 * non disponibili. Visibile a tutti i ruoli, ma la libera professione dei colleghi resta a chi
 * pianifica, che è anche l'unico a poter modificare le voci e solo sulle settimane non archiviate.
 */
function renderWeekAbsenceFooter(weekBlock, weekStart, weekDays) {
  const row = weekBlock.querySelector(`#tfoot-absent-${weekStart}`);
  if (!row) return;
  const doctors = plannerAbsenceRowDoctors();
  const chipsByDate = buildPlannerAbsenceChipsByDate(weekDays.map(d => d.date), doctors);
  const canManage = Boolean(canManagePlannerAbsenceRow());
  const canAct = !isWeekLocked(weekStart) && canManage;

  const cells = weekDays.map(d => {
    const chips = (chipsByDate.get(d.date) || []).filter(c => canManage || c.kind !== 'lp');
    const chipsHtml = chips.map(c => {
      const label = `${escapeAttr(c.sigla)}${c.apex ? `<sup>${escapeAttr(c.apex)}</sup>` : ''}`;
      const cls = `grid-absent-chip grid-absent-chip-${c.kind}`;
      if (!canAct) {
        return `<span class="${cls} is-readonly" title="${escapeAttr(c.title)}">${label}</span>`;
      }
      return `<button type="button" class="${cls}" data-date="${d.date}" data-doctor-id="${escapeAttr(c.doctorId)}" data-item-keys="${escapeAttr((c.itemKeys || []).join(','))}" title="${escapeAttr(c.title)}">${label}</button>`;
    }).join('');
    const addHtml = canAct
      ? `<button type="button" class="grid-absent-add" data-date="${d.date}" title="Aggiungi assenza o desiderata — ${escapeAttr(formatDateShort(d.date))}" aria-label="Aggiungi assenza o desiderata del ${escapeAttr(formatDateShort(d.date))}">+</button>`
      : '';
    const inner = (chipsHtml || addHtml)
      ? `<div class="grid-absent-cell-inner">${chipsHtml}${addHtml}</div>`
      : '<span class="grid-absent-empty">·</span>';
    const isWeekend = d.weekday === 'sab' || d.weekday === 'dom';
    return `<td class="grid-absent-cell${isWeekend ? ' grid-day-weekend' : ''}" data-date="${d.date}">${inner}</td>`;
  }).join('');

  row.innerHTML = `
    <th class="grid-row-head grid-absent-head" title="${escapeAttr(plannerAbsentRowLegend({ canManage }))}">
      <span class="grid-absent-head-title">Assenze</span>
      ${plannerAbsentRowLegendHtml({ canManage })}
    </th>
    ${cells}
  `;
}

/** Elenco medici per il passo «Per quale medico?» del popup aperto dal chip «+». */
function plannerAbsencePickerDoctors(dateStr) {
  const doctors = plannerAbsenceRowDoctors();
  const chips = buildPlannerAbsenceChipsByDate([dateStr], doctors).get(dateStr) || [];
  const hintByDoctor = new Map();
  for (const c of chips) {
    const key = String(c.doctorId);
    const prev = hintByDoctor.get(key);
    hintByDoctor.set(key, prev ? `${prev} · ${c.apex}` : c.apex);
  }
  return doctors.map(d => ({
    id: d.id,
    sigla: d.sigla || '',
    cognome: d.cognome || '',
    nome: d.nome || '',
    hint: hintByDoctor.get(String(d.id)) || '',
  }));
}

/** Rende il corpo della tabella "posti x giorni" per una settimana. */
function renderWeekTableBody(weekBlock, weekStart, weekDays, slotsFromCalendar, assignmentsByKey) {
  const thead = weekBlock.querySelector(`#thead-grid-${weekStart}`);
  const tbody = weekBlock.querySelector(`#tbody-grid-${weekStart}`);
  if (!thead || !tbody) return;

  // Slot della settimana corrente
  const weekSlots = slotsFromCalendar.filter(s => {
    return weekDays.some(d => d.date === s.date);
  });

  // Header: prima cella vuota + una per giorno
  thead.innerHTML = `
    <th class="grid-row-head">Slot</th>
    ${weekDays.map(d => {
      const isWeekend = d.weekday === 'sab' || d.weekday === 'dom';
      const festivoClass = isWeekend ? ' grid-day-weekend' : '';
      const letter = WEEKDAY_LETTER_UI[d.weekday] || d.weekday;
      return `<th class="grid-day-head${festivoClass}" data-date="${d.date}">
        <div class="grid-day-letter">${letter}</div>
        <div class="grid-day-date">${(d.date || '').slice(8, 10)}/${(d.date || '').slice(5, 7)}</div>
      </th>`;
    }).join('')}
  `;

  // Indipendente dagli slot: va resa anche nelle settimane senza posti di turno.
  renderWeekAbsenceFooter(weekBlock, weekStart, weekDays);

  const slotTypes = getSlotTypesForWeek(weekSlots);
  const canAddMissingSlots = canEditShifts() && !isWeekLocked(weekStart);
  const highlightedDoctors = plannerWeekHighlightedDoctors.get(String(weekStart || '')) || new Set();
  const doctorPaletteMap = buildDoctorHighlightPaletteMap(getState().doctors || []);

  if (slotTypes.length === 0) {
    tbody.innerHTML = `<tr><td class="grid-empty" colspan="${weekDays.length + 1}">Nessun posto di turno per questa settimana.</td></tr>`;
    return;
  }

  // Una cella per (data, etichetta riga): slot canonico (preferisce una chiave con assegnazione).
  const byKey = assignmentsByKey || {};
  const slotIdx = new Map();
  const byDateLabel = new Map();
  for (const s of weekSlots) {
    const labelKey = getPlannerTableRowLabelKey(s);
    const dk = `${s.date}|${labelKey}`;
    if (!byDateLabel.has(dk)) byDateLabel.set(dk, []);
    byDateLabel.get(dk).push(s);
  }
  for (const [dk, arr] of byDateLabel) {
    const sk = (x) => x.slotKey || x.id;
    const sorted = [...arr].sort((a, b) => sk(a).localeCompare(sk(b)));
    let canonical = sorted[0];
    for (const cand of sorted) {
      const asn = resolveAssignmentForSlot(cand, byKey);
      if (asn != null && asn !== '') {
        canonical = cand;
        break;
      }
    }
    slotIdx.set(dk, canonical);
  }

  const rowsHtml = slotTypes.map((st, rowIdx) => {
    const { name, time } = getSlotTypeRowLabel(st);
    const catClass = isNoraLikeSalaSlotType(st) ? 'nora' : badgeClassForSlotCategory(st.category);
    const ambRowClass = isAnesthesiaAmbulatorySlotType(st) ? ' grid-row-cat-amb' : '';
    const cells = weekDays.map(d => {
      const k = `${d.date}|${st.key}`;
      const slot = slotIdx.get(k);
      if (!slot) {
        if (!canAddMissingSlots) {
          return `<td class="grid-cell grid-cell-na" aria-disabled="true">·</td>`;
        }
        const derivedUocId = st.uocId || CATEGORY_TO_UOC[st.category] || '';
        const activityType = st.activityType || 'clinico';
        return `<td class="grid-cell grid-cell-na add-slot-cell" data-date="${d.date}" data-time-slot="${escapeAttr(st.timeSlot)}" data-category="${escapeAttr(st.category)}" data-uoc-id="${escapeAttr(derivedUocId)}" data-name="${escapeAttr(st.name || '')}" data-activity-type="${escapeAttr(activityType)}" title="Aggiungi slot in questa cella">＋</td>`;
      }
      const slotCatClass = isNoraLikeSalaSlotType(slot) ? 'nora' : badgeClassForSlotCategory(slot.category, slot);
      const repClass = isReperibilitaSlotType(slot) ? ' cat-bg-rep-slot' : '';
      const tiNotteClass = slot.category === 'terapia_intensiva' && slot.timeSlot === TIME_SLOTS.NOTTE ? ' cat-bg-ti-notte' : '';
      const ambClass = isAnesthesiaAmbulatorySlotType(slot) ? ' cat-bg-amb' : '';
      const assignedDoctor = resolveAssignmentForSlot(slot, byKey);
      const hl = assignedDoctor && highlightedDoctors.has(String(assignedDoctor))
        ? doctorHighlightPalette(assignedDoctor, doctorPaletteMap)
        : null;
      const hlClass = hl ? ' slot-highlighted-doctor' : '';
      const hlStyle = hl ? ` style="--hl-bg:${hl.bg};--hl-fg:${hl.fg};--hl-border:${hl.border};"` : '';
      const slotKey = slot.slotKey || slot.id;
      const importIssue = getShiftImportIssueBySlotOrDate(
        slotKey,
        slot.date,
        slot.timeSlot,
        slot.category,
        slot.name,
        slot.plannerCellKey || ''
      );
      const warningClass = importIssue ? ' slot-import-warning-cell' : '';
      return `<td class="grid-cell drop-zone cat-bg cat-bg-${slotCatClass}${repClass}${tiNotteClass}${ambClass}${hlClass}${warningClass}" data-date="${d.date}" data-time="${slot.timeSlot}"${hlStyle}>${renderSlotCellTable(slot, slotsFromCalendar, importIssue)}</td>`;
    }).join('');
    return `
      <tr class="grid-row" data-row-idx="${rowIdx}">
        <th class="grid-row-head">
          <span class="grid-row-label">
            <span class="badge badge-${catClass} grid-row-cat${ambRowClass}">${name}</span>
            <span class="grid-row-time">${time}</span>
          </span>
        </th>
        ${cells}
      </tr>
    `;
  }).join('');

  tbody.innerHTML = rowsHtml;
}

/** Rende una cella slot in vista tabella: compatta, solo sigla, con select sovrapposta on-demand. */
function renderSlotCellTable(slot, allSlots, importIssue = null) {
  const slotKey = slot.slotKey || slot.id;
  const st = getState();
  const canEdit = canEditShifts();
  const assignment = resolveAssignmentForSlot(slot, st.shiftAssignmentsByKey || {});
  const doctors = st.doctors;
  const doctor =
    assignment != null && assignment !== ''
      ? doctors.find(d => String(d.id) === String(assignment))
      : null;
  const isUncovered = assignment == null || assignment === '';
  const slotWeekLocked = isWeekLocked(getWeekStart(slot.date));
  const assignmentLocked = Boolean(st.lockedAssignmentsByKey?.[slotKey]);
  const isLocked = assignmentLocked || slotWeekLocked;

  const slotReadOnly = slotWeekLocked || !canEdit || assignmentLocked;
  const readonlyHint = !canEdit
    ? 'Solo lettura'
    : slotWeekLocked
      ? 'Settimana archiviata'
      : assignmentLocked
        ? 'Turno lucchettato: sblocca per modificare l\'assegnazione'
        : '';
  let selectHtml;
  if (slotReadOnly) {
    selectHtml = `<select class="slot-assign-select" data-slot-id="${slot.id}" data-slot-key="${slotKey}" disabled title="${escapeAttr(readonlyHint)}">
        <option value="">—</option>
        ${doctor ? `<option value="${doctor.id}" selected>${doctor.sigla} ${doctor.cognome}</option>` : ''}
      </select>`;
  } else {
    // Segnaposto: l'elenco medici viene generato alla prima apertura della cella
    // (ensurePlannerSlotSelectOptions). L'option del medico assegnato resta qui, cosi'
    // `select.value` riflette lo stato e `revertPlannerSlotSelect` sa cosa ripristinare
    // anche su una cella mai aperta.
    selectHtml = `
      <select class="slot-assign-select" data-slot-id="${slot.id}" data-slot-key="${slotKey}" data-options-pending="1">
        <option value="">— Nessuna assegnazione —</option>
        ${doctor ? `<option value="${doctor.id}" selected>${doctor.sigla} ${doctor.cognome}</option>` : ''}
      </select>
    `;
  }

  const dndLocked = isLocked;
  const doctorBadge = doctor
    ? `<span class="badge badge-draggable grid-sigla" draggable="false" data-slot-id="${slotKey}" data-doctor-id="${doctor.id}" title="${slotReadOnly ? escapeAttr(readonlyHint) : dndLocked ? `${doctor.sigla} ${doctor.cognome} · sblocca il turno per trascinare` : `${doctor.sigla} ${doctor.cognome} · click per cambiare; tieni premuto ~0,1 s sulla sigla poi trascina`}">${doctor.sigla}</span>`
    : `<span class="grid-empty-mark" title="Slot non assegnato">—</span>`;

  // Settimana archiviata: lo dice gia' il badge "Archiviata" nell'header, e qui il lucchetto
  // comparirebbe su ogni cella assegnata senza essere azionabile. Si mostra solo quando e' il
  // singolo turno a essere bloccato.
  const lockIcon = (canEdit && doctor && !slotWeekLocked) ? `
    <span class="slot-lock-icon ${assignmentLocked ? 'locked' : 'unlocked'}" data-slot-key="${slotKey}" title="${assignmentLocked ? 'Sblocca turno' : 'Blocca turno'}">${assignmentLocked ? '🔒' : '🔓'}</span>
  ` : '';
  const issueDateTxt = formatDateItDash(importIssue?.date || slot?.date || '');
  const issueDoctorSigla = getIssueDoctorSigla(importIssue, doctors);
  const warningTitle = importIssue
    ? [issueDateTxt ? `Data: ${issueDateTxt}` : '', issueDoctorSigla ? `Medico: ${issueDoctorSigla}` : '', importIssue.reason_message || 'Assegnazione Excel non applicata']
      .filter(Boolean)
      .join(' · ')
    : '';
  const warningIcon = importIssue ? `
    <span class="slot-import-warning-icon" title="${warningTitle.replace(/"/g, '&quot;')}">⚠</span>
  ` : '';
  const warningClass = importIssue ? ' slot-import-warning' : '';

  return `
    <div class="slot-cell-inner view-table ${isUncovered ? 'slot-uncovered' : ''}${warningClass}">
      ${doctorBadge}
      ${lockIcon}
      ${warningIcon}
      ${selectHtml}
    </div>
  `;
}

/** Evita selezione testo nella pagina durante il drag HTML5 (altrimenti il movimento evidenzia testo). */
function blockSelectionDuringPlannerDrag(ev) {
  ev.preventDefault();
}

function setupDragAndDrop(container) {
  let draggedSlotId = null;
  let draggedDoctorId = null;

  // Use delegation on container
  container.addEventListener('dragstart', e => {
    if (e.target.classList.contains('badge-draggable')) {
      plannerDndCollapseAssignmentKeys = false;
      const isTableSigla = e.target.classList.contains('grid-sigla') && e.target.closest('.slot-cell-inner.view-table');
      const tableDragOk = tableDragArmed || e.target.classList.contains('table-drag-armed');
      if (isTableSigla && !tableDragOk) {
        e.preventDefault();
        return;
      }
      if (!isTableSigla && e.target.getAttribute('draggable') === 'false') {
        e.preventDefault();
        return;
      }
      draggedSlotId = e.target.dataset.slotId;
      draggedDoctorId = e.target.dataset.doctorId;
      if (isPlannerSlotKeyDnDLocked(draggedSlotId)) {
        e.preventDefault();
        return;
      }
      plannerDndCollapseAssignmentKeys = !!isTableSigla;
      if (isTableSigla) tableDragStarted = true;
      try {
        window.getSelection?.()?.removeAllRanges();
      } catch (_) {}
      document.body.classList.add('planner-dnd-active');
      document.addEventListener('selectstart', blockSelectionDuringPlannerDrag, true);
      e.dataTransfer.effectAllowed = 'move';
      const payload = JSON.stringify({ slotId: draggedSlotId, doctorId: draggedDoctorId });
      e.dataTransfer.setData('text/plain', payload);
      try {
        e.dataTransfer.setData('application/json', payload);
      } catch (_) {}
      plannerDnDLastPayload = { slotId: draggedSlotId, doctorId: String(draggedDoctorId) };
      e.target.classList.add('dragging');
    }
  });

  container.addEventListener('dragend', e => {
    if (e.target.classList.contains('badge-draggable')) {
      clearPlannerDnDDragOver(container);
      document.body.classList.remove('planner-dnd-active');
      document.removeEventListener('selectstart', blockSelectionDuringPlannerDrag, true);
      e.target.classList.remove('dragging');
      if (e.target.classList.contains('grid-sigla')) {
        if (tableDragArmTimer != null) {
          clearTimeout(tableDragArmTimer);
          tableDragArmTimer = null;
        }
        tableDragPointerDown = false;
        e.target.classList.remove('table-drag-armed');
        const innerEnd = e.target.closest('.slot-cell-inner.view-table');
        const selEnd = innerEnd?.querySelector('.slot-assign-select');
        const skEnd = e.target.dataset.slotId;
        if (selEnd && !selEnd.disabled && skEnd && !isPlannerSlotKeyDnDLocked(skEnd)) {
          e.target.draggable = false;
        }
        tableDragArmed = false;
        tableDragStarted = false;
        tableDragPointerBadge = null;
        tableSkipNextCellClick = true;
      }
      // Dopo drop: il browser può emettere dragend nello stesso turno; ritarda così drop legge ancora dragged*.
      setTimeout(() => {
        draggedSlotId = null;
        draggedDoctorId = null;
        plannerDnDLastPayload = null;
        plannerDndCollapseAssignmentKeys = false;
      }, 0);
    }
  });

  container.addEventListener('dragenter', e => {
    const inner = plannerDnDTargetInner(e);
    if (!inner) return;
    const select = inner.querySelector('.slot-assign-select');
    if (select?.disabled) return;
    const slotKey = select?.dataset.slotKey;
    if (slotKey && isPlannerSlotKeyDnDLocked(slotKey)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  });

  container.addEventListener('dragover', e => {
    const inner = plannerDnDTargetInner(e);
    if (!inner) return;
    const select = inner.querySelector('.slot-assign-select');
    if (select?.disabled) return;
    const slotKey = select?.dataset.slotKey;
    if (slotKey && isPlannerSlotKeyDnDLocked(slotKey)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    inner.classList.add('drag-over');
  });

  container.addEventListener('dragleave', e => {
    const inner = plannerDnDTargetInner(e);
    if (!inner) return;
    const related = e.relatedTarget;
    if (related && inner.contains(related)) return;
    inner.classList.remove('drag-over');
  });

  container.addEventListener('drop', async e => {
    e.preventDefault();
    clearPlannerDnDDragOver(container);
    const parsed = plannerDragPayloadFromDataTransfer(e, draggedSlotId, draggedDoctorId);
    const fromSlotKey = parsed.slotId || plannerDnDLastPayload?.slotId;
    const fromDoctorId = (parsed.doctorId || plannerDnDLastPayload?.doctorId)
      ? String(parsed.doctorId || plannerDnDLastPayload?.doctorId).trim()
      : '';
    const targetInner = plannerDnDTargetInner(e);
    if (!targetInner || !fromSlotKey || !fromDoctorId) return;
    const targetSelect = targetInner.querySelector('.slot-assign-select');
    if (!targetSelect) return;
    const targetSlotKey = targetSelect.dataset.slotKey;
    if (targetSlotKey === fromSlotKey) return;
    if (isPlannerSlotKeyDnDLocked(fromSlotKey) || isPlannerSlotKeyDnDLocked(targetSlotKey)) return;
    const state = getState();
    const wf = state.shiftPlanWeekFrom || getMondayOfWeek(new Date().toISOString().slice(0, 10));
    const wt = state.shiftPlanWeekTo || wf;
    const allSlots = buildAllSlotsForPlannerDrop(wf, wt, targetSlotKey, fromSlotKey);
    const targetSlot = allSlots.find(s => (s.slotKey || s.id) === targetSlotKey);
    const sourceSlot = allSlots.find(s => (s.slotKey || s.id) === fromSlotKey);
    if (!targetSlot) {
      alert('Slot di destinazione non trovato. Verifica l\'intervallo settimane nel Piano turni o aggiorna la pagina.');
      return;
    }
    const byKey = state.shiftAssignmentsByKey || {};
    const targetDoctorId = resolveAssignmentForSlot(targetSlot, byKey) || null; // swap se già occupato (stessa risoluzione chiavi della UI)
    const keysRm = assignmentKeysForSamePlannerRow(sourceSlot, fromSlotKey, allSlots, byKey, plannerDndCollapseAssignmentKeys);
    // Chiave logica per ogni cella toccata: il DB può averle salvate con una
    // slot_key diversa (rebind -tpl/-mod → lgk/-oneoff) e la DELETE per chiave
    // esatta fallirebbe in silenzio lasciando l'origine occupata.
    const dndLogicalKeys = {};
    for (const k of [...keysRm, fromSlotKey, targetSlotKey]) {
      if (!k || dndLogicalKeys[k]) continue;
      const slotForKey = allSlots.find(s => (s.slotKey || s.id) === k);
      const lgk = slotForKey ? stableCalendarSlotKey(slotForKey) : '';
      if (lgk) dndLogicalKeys[k] = lgk;
    }
    const [yr] = targetSlot.date.split('-').map(Number);
    const maxDays = getFestiviCountInWeek(getWeekStart(targetSlot.date), yr) > 0 ? 4 : 5;
    const baseOpts = {
      slots: allSlots,
      allowManualOverride: true,
      allowOverrideZeroPercent: true,
      maxDaysPerWeek: maxDays,
    };
    const simulatedForDest = { ...state.shiftAssignmentsByKey };
    for (const k of keysRm) delete simulatedForDest[k];
    simulatedForDest[targetSlotKey] = fromDoctorId;
    const pendingAbsenceClears = [];
    let usedNightSmontoDest = false;
    let resDest = canAssignDoctorToSlot(fromDoctorId, targetSlot, {
      ...baseOpts,
      assignmentsByKey: simulatedForDest,
    });
    if (!resDest.ok && resDest.reasonCode === 'direzione_not_enabled') {
      const okDirezione = await confirmDirezioneManualOverride(fromDoctorId, targetSlot);
      if (!okDirezione) return;
      resDest = canAssignDoctorToSlot(fromDoctorId, targetSlot, {
        ...baseOpts,
        assignmentsByKey: simulatedForDest,
        allowDirezioneManualOverride: true,
      });
    }
    if (!resDest.ok && resDest.reasonCode === 'night_rest_smonto') {
      const okSmonto = await confirmNightRestSmontoManualOverride();
      if (!okSmonto) return;
      resDest = canAssignDoctorToSlot(fromDoctorId, targetSlot, {
        ...baseOpts,
        assignmentsByKey: simulatedForDest,
        allowNightRestSmontoManualOverride: true,
      });
      usedNightSmontoDest = true;
    }
    if (!resDest.ok && resDest.reasonCode === 'night_consecutive') {
      const okConsecutive = await confirmNightConsecutiveManualOverride();
      if (!okConsecutive) return;
      resDest = canAssignDoctorToSlot(fromDoctorId, targetSlot, {
        ...baseOpts,
        assignmentsByKey: simulatedForDest,
        allowNightConsecutiveManualOverride: true,
        allowNightRestSmontoManualOverride: true,
      });
      usedNightSmontoDest = true;
    }
    if (!resDest.ok && resDest.reasonCode === 'absence_desiderata_override') {
      const okAbsence = await confirmAbsenceDesiderataManualOverride(fromDoctorId, targetSlot);
      if (!okAbsence) return;
      resDest = canAssignDoctorToSlot(fromDoctorId, targetSlot, {
        ...baseOpts,
        assignmentsByKey: simulatedForDest,
        allowAbsenceDesiderataManualOverride: true,
      });
      pendingAbsenceClears.push({ doctorId: fromDoctorId, slot: targetSlot });
    }
    if (!resDest.ok) {
      const incompatibleIds = [fromDoctorId];
      let detail = '';
      if (targetDoctorId) {
        const simEmptyDest = { ...state.shiftAssignmentsByKey };
        for (const k of keysRm) delete simEmptyDest[k];
        delete simEmptyDest[targetSlotKey];
        const resTargetOnEmptyDest = canAssignDoctorToSlot(targetDoctorId, targetSlot, {
          ...baseOpts,
          assignmentsByKey: simEmptyDest,
        });
        if (!resTargetOnEmptyDest.ok) {
          incompatibleIds.push(targetDoctorId);
          if (resTargetOnEmptyDest.reason && resTargetOnEmptyDest.reason !== resDest.reason) {
            detail = ` — ${resTargetOnEmptyDest.reason}`;
          }
        }
      }
      alert(formatDndIncompatibilityAlert(`${resDest.reason}${detail}`, incompatibleIds));
      return;
    }
    let usedNightSmontoSource = false;
    if (targetDoctorId && sourceSlot) {
      const simulatedForSource = { ...simulatedForDest, [fromSlotKey]: targetDoctorId };
      let resSource = canAssignDoctorToSlot(targetDoctorId, sourceSlot, {
        ...baseOpts,
        assignmentsByKey: simulatedForSource,
      });
      if (!resSource.ok && resSource.reasonCode === 'direzione_not_enabled') {
        const okDirezione = await confirmDirezioneManualOverride(targetDoctorId, sourceSlot);
        if (!okDirezione) return;
        resSource = canAssignDoctorToSlot(targetDoctorId, sourceSlot, {
          ...baseOpts,
          assignmentsByKey: simulatedForSource,
          allowDirezioneManualOverride: true,
        });
      }
      if (!resSource.ok && resSource.reasonCode === 'night_rest_smonto') {
        const okSmonto = await confirmNightRestSmontoManualOverride();
        if (!okSmonto) return;
        resSource = canAssignDoctorToSlot(targetDoctorId, sourceSlot, {
          ...baseOpts,
          assignmentsByKey: simulatedForSource,
          allowNightRestSmontoManualOverride: true,
        });
        usedNightSmontoSource = true;
      }
      if (!resSource.ok && resSource.reasonCode === 'night_consecutive') {
        const okConsecutive = await confirmNightConsecutiveManualOverride();
        if (!okConsecutive) return;
        resSource = canAssignDoctorToSlot(targetDoctorId, sourceSlot, {
          ...baseOpts,
          assignmentsByKey: simulatedForSource,
          allowNightConsecutiveManualOverride: true,
          allowNightRestSmontoManualOverride: true,
        });
        usedNightSmontoSource = true;
      }
      if (!resSource.ok && resSource.reasonCode === 'absence_desiderata_override') {
        const okAbsence = await confirmAbsenceDesiderataManualOverride(targetDoctorId, sourceSlot);
        if (!okAbsence) return;
        resSource = canAssignDoctorToSlot(targetDoctorId, sourceSlot, {
          ...baseOpts,
          assignmentsByKey: simulatedForSource,
          allowAbsenceDesiderataManualOverride: true,
        });
        pendingAbsenceClears.push({ doctorId: targetDoctorId, slot: sourceSlot });
      }
      if (!resSource.ok) {
        alert(formatDndIncompatibilityAlert(
          `Lo scambio non è possibile: ${resSource.reason}`,
          [targetDoctorId],
        ));
        return;
      }
    }
    // Popup ripartizione 0%: solo barre profilo (Direzione = solo flag Abilità Direzione).
    const doctor = getState().doctors.find(d => String(d.id) === String(fromDoctorId));
    const distKey = zeroPercentWarningDistKey(targetSlot);
    const pct = distKey != null && doctor ? doctor.distribuzione?.[distKey] : undefined;
    const isTI = targetSlot.category === 'terapia_intensiva';
    const isTINightOrWeekend = isTI && (targetSlot.timeSlot === 'notte' || ['sab', 'dom'].includes(targetSlot.weekday));
    if (pct === 0 && !isTINightOrWeekend) {
      const areaLabel = zeroPercentWarningAreaLabel(targetSlot, distKey);
      const ok = await confirmInModal(
        `Attenzione: stai assegnando un medico con 0% nella ripartizione di ${areaLabel}. Continuare?`,
        { title: 'Ripartizione 0%', okLabel: 'Continua', cancelLabel: 'Annulla' },
      );
      if (!ok) return;
    }
    const docFrom = getState().doctors.find(d => String(d.id) === String(fromDoctorId));
    const docTgt = targetDoctorId
      ? getState().doctors.find(d => String(d.id) === String(targetDoctorId))
      : null;
    const allowDirMove = isDirezioneSlotKind(targetSlot) && docFrom && !docFrom.abilitaDirezione;
    const allowDirMoveRestore = sourceSlot && isDirezioneSlotKind(sourceSlot) && docFrom && !docFrom.abilitaDirezione;
    const allowDirSwapToTarget = isDirezioneSlotKind(targetSlot) && docFrom && !docFrom.abilitaDirezione;
    const allowDirSwapToSource = sourceSlot && isDirezioneSlotKind(sourceSlot) && docTgt && !docTgt.abilitaDirezione;
    const allowNightSmontoMoveRestore = usedNightSmontoDest && sourceSlot
      ? needsNightRestSmontoManualSlot(fromDoctorId, sourceSlot, allSlots, { ...state.shiftAssignmentsByKey, [fromSlotKey]: fromDoctorId }, maxDays)
      : false;
    try {
      for (const clear of pendingAbsenceClears) {
        await clearAbsenceDesiderataConflictForSlot(clear.doctorId, clear.slot);
      }
      const dropTargetTable = !!targetInner.closest('.shift-table-grid[data-view-mode="table"]');
      if (targetDoctorId && sourceSlot) {
        await swapAssignmentsByKey(
          fromSlotKey,
          targetSlotKey,
          sourceSlot,
          targetSlot,
          fromDoctorId,
          targetDoctorId,
          getAutoLockManualAssignments(),
          {
            allowDirezioneSourceOnTarget: allowDirSwapToTarget,
            allowDirezioneTargetOnSource: allowDirSwapToSource,
            allowNightRestSmontoSourceOnTarget: usedNightSmontoDest,
            allowNightRestSmontoTargetOnSource: usedNightSmontoSource,
          },
        );
        if (dropTargetTable) {
          await syncPlannerTableSlotSiblingsFromPrimary(targetSlot, targetSlotKey, fromDoctorId, allSlots);
          await syncPlannerTableSlotSiblingsFromPrimary(sourceSlot, fromSlotKey, targetDoctorId, allSlots);
        }
      } else {
        await moveAssignmentBetweenKeys(
          fromSlotKey,
          targetSlotKey,
          fromDoctorId,
          targetSlot,
          getAutoLockManualAssignments(),
          keysRm,
          {
            allowDirezioneManualOverride: allowDirMove,
            allowDirezioneManualOverrideRestore: allowDirMoveRestore,
            allowNightRestSmontoManualOverride: usedNightSmontoDest,
            allowNightRestSmontoManualOverrideRestore: allowNightSmontoMoveRestore,
            logicalKeys: dndLogicalKeys,
          },
        );
        if (dropTargetTable) await syncPlannerTableSlotSiblingsFromPrimary(targetSlot, targetSlotKey, fromDoctorId, allSlots);
      }
      await refreshShiftAssignmentsFromApi();
      refreshPlannerWeekStats(container);
    } catch (err) {
      alert('Errore: ' + err.message);
      // Riallinea la griglia al DB: dopo un errore parziale la cella potrebbe
      // risultare libera nella UI ma ancora occupata lato server.
      await refreshShiftAssignmentsFromApi();
      await renderShiftPlanner(container);
    }
  });
}

/**
 * Option della tendina di assegnazione: una validazione per medico, piu' i re-check dei
 * vincoli derogabili con conferma. Costa ~8 ms a cella, ed e' il motivo per cui non si
 * generano al render della griglia: la tendina e' `display:none` finche' non si clicca la
 * cella, quindi farlo per tutte le celle era lavoro interamente sprecato.
 * @see ensurePlannerSlotSelectOptions
 */
function buildSlotAssignOptionsHTML(slot, allSlots) {
  const st = getState();
  const doctors = st.doctors;
  const assignment = resolveAssignmentForSlot(slot, st.shiftAssignmentsByKey || {});
  const isUncovered = assignment == null || assignment === '';
  const currentByKey = st.shiftAssignmentsByKey || {};
  const [yr] = slot.date.split('-').map(Number);
  const maxDays = getFestiviCountInWeek(getWeekStart(slot.date), yr) > 0 ? 4 : 5;
  const opts = { slots: allSlots, assignmentsByKey: currentByKey, allowManualOverride: true, allowOverrideZeroPercent: true, maxDaysPerWeek: maxDays };
  const escReason = (r) => (r || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&/g, '&amp;');
  const chunks = { ok: [], direzioneConfirm: [], smonto: [], nightConsecutive: [], absenceDesiderata: [], bad: [] };
  for (const d of doctors) {
    const selected = String(assignment) === String(d.id) ? ' selected' : '';
    const res = canAssignDoctorToSlot(d.id, slot, opts);
    const isAssignedHere = String(assignment) === String(d.id);
    const smontoGate = !res.ok && res.reasonCode === 'night_rest_smonto'
      && canAssignDoctorToSlot(d.id, slot, { ...opts, allowNightRestSmontoManualOverride: true }).ok;
    const direzioneManualGate =
      !res.ok
      && res.reasonCode === 'direzione_not_enabled'
      && canAssignDoctorToSlot(d.id, slot, { ...opts, allowDirezioneManualOverride: true }).ok;
    const nightConsecutiveGate = !res.ok && res.reasonCode === 'night_consecutive'
      && canAssignDoctorToSlot(d.id, slot, { ...opts, allowNightConsecutiveManualOverride: true, allowNightRestSmontoManualOverride: true }).ok;
    const absenceDesiderataGate = !res.ok && res.reasonCode === 'absence_desiderata_override'
      && canAssignDoctorToSlot(d.id, slot, { ...opts, allowAbsenceDesiderataManualOverride: true }).ok;
    const label0 = `${d.sigla} ${d.cognome}`;
    if (res.ok || isAssignedHere) {
      const reason = res.reason || '';
      const disabled = !isAssignedHere && !res.ok;
      const title = disabled ? reason : '';
      let label = label0;
      if (disabled && reason) label += ` — ${escReason(reason)}`;
      chunks.ok.push(
        `<option value="${d.id}"${selected}${disabled ? ' disabled' : ''} title="${(title || '').replace(/"/g, '&quot;')}">${label}</option>`,
      );
    } else if (smontoGate) {
      const label = `${label0} — smonto post-notte`;
      chunks.smonto.push(
        `<option value="${d.id}"${selected} style="color: var(--text-secondary);" title="Richiede conferma all'assegnazione">${label}</option>`,
      );
    } else if (direzioneManualGate) {
      const label = `${label0} — senza Abilità Direzione`;
      chunks.direzioneConfirm.push(
        `<option value="${d.id}"${selected} style="color: var(--text-secondary);" title="Richiede conferma all'assegnazione (solo manuale)">${label}</option>`,
      );
    } else if (nightConsecutiveGate) {
      const label = `${label0} — notte adiacente`;
      chunks.nightConsecutive.push(
        `<option value="${d.id}"${selected} style="color: var(--text-secondary);" title="Notte il giorno prima/dopo: richiede conferma">${label}</option>`,
      );
    } else if (absenceDesiderataGate) {
      const label = `${label0} — assenza/desiderata`;
      chunks.absenceDesiderata.push(
        `<option value="${d.id}"${selected} style="color: var(--text-secondary);" title="Incompatibilità assenza/desiderata: conferma per assegnare e rimuovere dal piano assenze">${label}</option>`,
      );
    } else {
      const reason = res.reason || '';
      const disabled = !isAssignedHere;
      const title = disabled ? reason : '';
      let label = label0;
      if (disabled && reason) label += ` — ${escReason(reason)}`;
      chunks.bad.push(
        `<option value="${d.id}"${selected}${disabled ? ' disabled' : ''} title="${(title || '').replace(/"/g, '&quot;')}">${label}</option>`,
      );
    }
  }
  const smontoGroup = chunks.smonto.length
    ? `<optgroup label="── Smonto post-notte (conferma) ──">${chunks.smonto.join('')}</optgroup>`
    : '';
  const direzioneGroup = chunks.direzioneConfirm.length
    ? `<optgroup label="── Direzione: senza Abilità Direzione (conferma) ──">${chunks.direzioneConfirm.join('')}</optgroup>`
    : '';
  const nightConsecutiveGroup = chunks.nightConsecutive.length
    ? `<optgroup label="── Notti consecutive (conferma) ──">${chunks.nightConsecutive.join('')}</optgroup>`
    : '';
  const absenceDesiderataGroup = chunks.absenceDesiderata.length
    ? `<optgroup label="── Assenza/Desiderata: rimuove dal piano (conferma) ──">${chunks.absenceDesiderata.join('')}</optgroup>`
    : '';
  const removeSlotOption = isUncovered
    ? '<option value="__separator__" disabled>────────</option><option value="__remove_slot__">🗑 Rimuovi Slot</option>'
    : '';
  return `
        <option value="">— Nessuna assegnazione —</option>
        ${removeSlotOption}
        ${chunks.ok.join('')}
        ${direzioneGroup}
        ${smontoGroup}
        ${nightConsecutiveGroup}
        ${absenceDesiderataGroup}
        ${chunks.bad.join('')}
  `;
}

// End of file
